/**
 * Public Tracking Page — Suivi en temps réel chauffeur
 * Route: /track/:token
 * 
 * Phase 2+3: Live tracking + Timeline & Replay
 * PT-07: Public page /track/{token}
 * PT-08: Live tracking (10s polling)
 * PT-11: Multi-zoom timeline (Day → Hour → 15min → Minute)
 * PT-12: Fluid cursor with GPS interpolation
 * PT-13: Combined Live + Replay mode
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { GoogleMap, useJsApiLoader, Marker, Polyline, TrafficLayer } from '@react-google-maps/api';
import {
  MapPin, Clock, User, Navigation, Car, AlertTriangle, Radio,
  ChevronDown, ChevronUp, CheckCircle, Play, Pause, SkipBack,
  SkipForward, ZoomIn, ZoomOut, Gauge, Eye, Activity, Maximize2, ShieldOff,
  Satellite, Download, Timer, Flame, RefreshCw, MessageSquare, Send, Video
} from 'lucide-react';
import config from 'config';
import { processGpsTrack, computeBearing } from '../utils/gpsProcessing';
import { GpsBuffer, smoothHeading, haversineM, bearing as computeBearingSmooth, SMOOTHING_CONFIG } from '../utils/liveSmoothing';
import GpsVideoExportModal from '../components/admin/GpsVideoExportModal';
import MissionAnalysisModal from '../components/admin/MissionAnalysisModal';

const API = `${config.API_URL}/api`;
const MAPS_KEY = config.GOOGLE_MAPS_API_KEY;
const LIBRARIES = ['places'];
const WS_RECONNECT_DELAY = 3000;

// Speed-coloured polyline — EXCESS over the road speed limit (same as the video export):
//   green = within limit · orange = +1..10 km/h · red = > +10 km/h
// Stops are static points (no segment) → never red on the polyline.
const SPEED_TRACE_COLORS = { green: '#10B981', orange: '#F59E0B', red: '#EF4444' };
const SPEED_TRACE_LEGEND = [
  { color: '#10B981', label: 'Vitesse respectée' },
  { color: '#F59E0B', label: 'Excès +1 à 10 km/h' },
  { color: '#EF4444', label: 'Excès > 10 km/h' },
];

// SVG car icon for replay marker (top-down view, points UP = 0° heading)
// Uses only M, L, C, Z commands (Google Maps Symbol compatible)
const CAR_SVG_PATH = 'M 12 2 C 8.5 2 6 4 6 7 L 6 17 C 6 18.7 7.3 20 9 20 L 15 20 C 16.7 20 18 18.7 18 17 L 18 7 C 18 4 15.5 2 12 2 Z M 9 5 L 15 5 L 15 7 L 9 7 Z';

const mapStyles = [
  { elementType: "geometry", stylers: [{ color: "#1a1a2e" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#0a0a14" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#8a8a9a" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#252540" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#2d2d4a" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#0a3d62" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#3a7ca5" }] },
  { featureType: "landscape.natural.terrain", elementType: "geometry", stylers: [{ color: "#1e1e36" }] },
  { featureType: "poi", elementType: "geometry", stylers: [{ color: "#16162a" }] },
  { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#1a2e1a" }] },
];

const STATUS_CONFIG = {
  // Chauffeur workflow statuses (Ticket 7.2)
  assigned: { label: 'Assignée', color: '#3B82F6' },
  accepted: { label: 'Acceptée', color: '#10B981' },
  en_route: { label: 'En approche', color: '#F59E0B' },
  waiting_for_pickup: { label: 'Attente PEC', color: '#FB923C' },
  client_on_board: { label: 'Client à bord', color: '#06B6D4' },
  dropped_off: { label: 'Client déposé', color: '#A855F7' },
  arret: { label: 'En arrêt', color: '#EAB308' },
  completed: { label: 'Terminée', color: '#22C55E' },
  cancelled: { label: 'Annulée', color: '#EF4444' },
  // Dispatch statuses
  proposed: { label: 'Proposée', color: '#F59E0B' },
  en_approche: { label: 'En approche', color: '#F59E0B' },
  en_attente: { label: 'En attente', color: '#F97316' },
  en_attente_pec: { label: 'Attente PEC', color: '#FB923C' },
  prise_en_charge: { label: 'Client à bord', color: '#06B6D4' },
  depose_client: { label: 'Client déposé', color: '#A855F7' },
  unassigned: { label: 'Non assignée', color: '#6B7280' },
  // Legacy
  CONFIRMED: { label: 'Confirmée', color: '#3B82F6' },
  EN_COURS: { label: 'En cours', color: '#06B6D4' },
  TERMINE: { label: 'Terminée', color: '#22C55E' },
  confirmed: { label: 'Confirmée', color: '#3B82F6' },
  on_site: { label: 'Sur place', color: '#F97316' },
};

const TIMELINE_STATUS_COLORS = {
  MISSION_ACCEPTEE: '#6366F1',       // Indigo — mission accepted
  EN_APPROCHE: '#3B82F6',            // Blue — driver en route
  EN_ATTENTE_PRISE_EN_CHARGE_CLIENT: '#F59E0B', // Amber — waiting for client
  ATTENTE_PRISE_EN_CHARGE: '#F59E0B',
  WAITING_FOR_PICKUP: '#F59E0B',
  PRISE_EN_CHARGE_CLIENT: '#10B981', // Green — client on board
  ARRET: '#EF4444',                  // Red — stop
  ARRET_AUTO_DETECTE: '#EF4444',
  RESUME_AFTER_STOP: '#14B8A6',      // Teal — resumed after stop
  DEPOSE_CLIENT: '#8B5CF6',          // Purple — client dropped off
  FIN_DE_MISSION: '#64748B',         // Slate — mission ended
  MISSION_ANNULEE: '#DC2626',
  MISSION_REFUSEE: '#DC2626',
};

const getEventStatusColor = (eventType) => TIMELINE_STATUS_COLORS[String(eventType || '').toUpperCase()] || '#94A3B8';

const withAlpha = (hex, alpha = '33') => {
  if (typeof hex !== 'string') return hex;
  if (hex.startsWith('#') && hex.length === 7) return `${hex}${alpha}`;
  return hex;
};

const getTimelineTheme = (status) => {
  const s = String(status || '').toLowerCase();
  if (['completed', 'termine', 'depose_client', 'dropped_off'].includes(s)) {
    return { accent: '#22C55E', soft: '#14532D' };
  }
  if (['cancelled', 'annulee', 'mission_annulee'].includes(s)) {
    return { accent: '#EF4444', soft: '#7F1D1D' };
  }
  if (['waiting_for_pickup', 'en_attente', 'en_attente_pec', 'on_site'].includes(s)) {
    return { accent: '#F59E0B', soft: '#78350F' };
  }
  if (['en_route', 'en_approche', 'accepted', 'assigned', 'confirmed'].includes(s)) {
    return { accent: '#3B82F6', soft: '#1E3A8A' };
  }
  return { accent: '#9CA3AF', soft: '#374151' };
};

const formatTime = (iso) => {
  if (!iso) return '--:--';
  try { return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }); }
  catch { return '--:--'; }
};
const formatDateTime = (iso) => {
  if (!iso) return '--';
  try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return '--'; }
};
const fmtMs = (ms) => new Date(ms).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Paris' });
const fmtMsShort = (ms) => new Date(ms).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Paris' });

const haversineMeters = (a, b) => {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad((b?.lat || 0) - (a?.lat || 0));
  const dLng = toRad((b?.lng || 0) - (a?.lng || 0));
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLng / 2);
  const h = s1 * s1 + Math.cos(toRad(a?.lat || 0)) * Math.cos(toRad(b?.lat || 0)) * s2 * s2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
};

const toTimestampMs = (ts) => {
  const ms = new Date(ts).getTime();
  return Number.isFinite(ms) ? ms : 0;
};

/**
 * Compute a reliable fingerprint of GPS segments based on:
 * - number of segments
 * - number of points per segment
 * - first & last timestamps per segment
 * This uniquely identifies the current state of pathSegments so we can
 * detect when the snap-to-road result no longer matches the GPS input.
 */
const computeSegmentsFingerprint = (segments) => {
  if (!segments || segments.length === 0) return '';
  const parts = segments.map(seg => {
    if (!seg || seg.length === 0) return '0';
    const firstTs = seg[0]?.timestamp || '';
    const lastTs = seg[seg.length - 1]?.timestamp || '';
    return `${seg.length}:${firstTs}:${lastTs}`;
  });
  return `${segments.length}|${parts.join('|')}`;
};

const getSourcePriority = (source) => {
  const s = String(source || '').toLowerCase();
  if (['tracker', 'webfleet', 'telem', 'fmb', 'obd', 'can'].some(k => s.includes(k))) return 0;
  if (['gps', 'device'].some(k => s.includes(k))) return 1;
  if (['mobile', 'app', 'phone'].some(k => s.includes(k))) return 3;
  if (s.includes('ws')) return 4;
  return 2;
};

const shouldPreferPoint = (incoming, current) => {
  if (!current) return true;
  const pIn = getSourcePriority(incoming?.source);
  const pCur = getSourcePriority(current?.source);
  if (pIn !== pCur) return pIn < pCur;

  const aIn = incoming?.accuracy == null ? Number.POSITIVE_INFINITY : Number(incoming.accuracy);
  const aCur = current?.accuracy == null ? Number.POSITIVE_INFINITY : Number(current.accuracy);
  if (aIn !== aCur) return aIn < aCur;

  const mIn = Number(incoming?.heading != null) + Number(incoming?.speed != null);
  const mCur = Number(current?.heading != null) + Number(current?.speed != null);
  if (mIn !== mCur) return mIn > mCur;

  return toTimestampMs(incoming?.timestamp) >= toTimestampMs(current?.timestamp);
};

const mergeLocationPoints = (existing, incoming) => {
  if (!incoming?.length) return existing;
  const merged = [...existing];

  incoming.forEach((point) => {
    if (!point?.timestamp) return;
    const last = merged[merged.length - 1];

    if (!last) {
      merged.push(point);
      return;
    }

    if (last.timestamp === point.timestamp) {
      if (shouldPreferPoint(point, last)) merged[merged.length - 1] = point;
      return;
    }

    const dt = Math.abs(toTimestampMs(point.timestamp) - toTimestampMs(last.timestamp));
    const dist = haversineMeters(last, point);
    if (dt <= 2000 && dist < 2) {
      if (shouldPreferPoint(point, last)) merged[merged.length - 1] = point;
      return;
    }

    merged.push(point);
  });

  return merged;
};

// ==========================================
// ZOOM LEVELS
// ==========================================
const ZOOM_DAY = 0;
const ZOOM_HOUR = 1;
const ZOOM_QUARTER = 2;
const ZOOM_MINUTE = 3;

const ZOOM_LABELS = ['Jour', 'Heure', '15 min', 'Minute'];

function generateTimeLabels(zoom, start, end) {
  const labels = [];
  const dur = end - start;
  if (dur <= 0) return labels;
  if (zoom === ZOOM_DAY) {
    const d = new Date(start); d.setMinutes(0, 0, 0);
    for (let h = 0; h <= 23; h += 2) {
      const t = new Date(d); t.setHours(h);
      const ms = t.getTime();
      if (ms >= start && ms <= end) labels.push({ ms, label: `${String(h).padStart(2, '0')}h`, ratio: (ms - start) / dur });
    }
  } else if (zoom === ZOOM_HOUR) {
    const step = 15 * 60 * 1000;
    let t = Math.ceil(start / step) * step;
    while (t <= end) { labels.push({ ms: t, label: fmtMsShort(t), ratio: (t - start) / dur }); t += step; }
  } else if (zoom === ZOOM_QUARTER) {
    const step = 60 * 1000;
    let t = Math.ceil(start / step) * step;
    while (t <= end) { labels.push({ ms: t, label: String(new Date(t).getMinutes()).padStart(2, '0'), ratio: (t - start) / dur }); t += step; }
  } else {
    const step = 10 * 1000;
    let t = Math.ceil(start / step) * step;
    while (t <= end) { labels.push({ ms: t, label: `${new Date(t).getMinutes()}:${String(new Date(t).getSeconds()).padStart(2, '0')}`, ratio: (t - start) / dur }); t += step; }
  }
  return labels;
}

function interpolateGPS(a, b, ratio) {
  if (!a || !b) return a || b || null;
  const r = Math.max(0, Math.min(1, ratio));
  return { lat: a.lat + (b.lat - a.lat) * r, lng: a.lng + (b.lng - a.lng) * r };
}

// ==========================================
// TIMELINE COMPONENT
// ==========================================
function TrackingTimeline({ points, speedSource, events, onPositionChange, onModeChange, isLiveMode, missionActiveStart, missionActiveEnd, missionStatus }) {
  const [zoom, setZoom] = useState(ZOOM_DAY);
  const [zoomRange, setZoomRange] = useState(null);
  const [cursor, setCursor] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [dragging, setDragging] = useState(false);
  const barRef = useRef(null);
  const animRef = useRef(null);
  const lastFrame = useRef(null);
  const timelineTheme = useMemo(() => getTimelineTheme(missionStatus), [missionStatus]);

  // Compute time range from points
  const pointMs = useMemo(() => points.map(p => new Date(p.timestamp).getTime()), [points]);
  const dataRange = useMemo(() => {
    if (!pointMs.length) return null;
    return { start: pointMs[0], end: pointMs[pointMs.length - 1] };
  }, [pointMs]);

  // ── Real speed source: raw GPS locations carry the true per-point speed (km/h).
  // The smoothed/snapped polyline points lose `speed`, so we resolve speed by
  // timestamp against the raw locations instead of recomputing via haversine
  // (which yields a near-constant value over evenly-resampled snapped points).
  const speedSrc = useMemo(() => {
    const src = (speedSource && speedSource.length ? speedSource : points) || [];
    return src
      .map(p => ({ ms: new Date(p.timestamp || p.recorded_at).getTime(), speed: Number(p.speed) || 0 }))
      .filter(p => Number.isFinite(p.ms))
      .sort((a, b) => a.ms - b.ms);
  }, [speedSource, points]);

  const speedAt = useCallback((ts) => {
    if (!speedSrc.length) return 0;
    if (ts <= speedSrc[0].ms) return Math.round(speedSrc[0].speed);
    if (ts >= speedSrc[speedSrc.length - 1].ms) return Math.round(speedSrc[speedSrc.length - 1].speed);
    let lo = 0, hi = speedSrc.length - 1;
    while (lo < hi - 1) { const mid = (lo + hi) >> 1; if (speedSrc[mid].ms <= ts) lo = mid; else hi = mid; }
    // Linear interpolation between the two surrounding raw speed samples.
    const a = speedSrc[lo], b = speedSrc[hi];
    const r = b.ms > a.ms ? (ts - a.ms) / (b.ms - a.ms) : 0;
    return Math.round(a.speed + (b.speed - a.speed) * r);
  }, [speedSrc]);

  // Aggregate stats (moving average excludes stops < 2 km/h, max filters GPS spikes > 200).
  const speedStats = useMemo(() => {
    if (!speedSrc.length) return { avg: 0, max: 0 };
    const moving = speedSrc.filter(p => p.speed >= 2 && p.speed <= 200);
    const avg = moving.length ? moving.reduce((s, p) => s + p.speed, 0) / moving.length : 0;
    const max = speedSrc.reduce((m, p) => (p.speed <= 200 && p.speed > m ? p.speed : m), 0);
    return { avg: Math.round(avg), max: Math.round(max) };
  }, [speedSrc]);

  // Derive timeline start/end from events: start at EN_APPROCHE, end at DEPOSE_CLIENT/FIN_DE_MISSION
  const eventDerivedRange = useMemo(() => {
    if (!events?.length) return null;
    const START_TYPES = ['EN_APPROCHE'];
    const END_TYPES = ['DEPOSE_CLIENT', 'FIN_DE_MISSION', 'MISSION_ANNULEE'];
    let startMs = null;
    let endMs = null;
    for (const evt of events) {
      if (!evt.timestamp) continue;
      const ms = new Date(evt.timestamp).getTime();
      if (!Number.isFinite(ms)) continue;
      const t = String(evt.type || '').toUpperCase();
      if (!startMs && START_TYPES.includes(t)) startMs = ms;
      if (END_TYPES.includes(t)) endMs = ms;
    }
    if (startMs) return { start: startMs, end: endMs || Date.now() };
    return null;
  }, [events]);

  // Mission date for display
  const missionDate = useMemo(() => {
    const ref = eventDerivedRange?.start || (dataRange?.start) || (missionActiveStart ? new Date(missionActiveStart).getTime() : null);
    if (!ref) return null;
    return new Date(ref).toLocaleDateString('fr-FR', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  }, [eventDerivedRange, dataRange, missionActiveStart]);

  const dayRange = useMemo(() => {
    // Priority 1: Event-derived range (EN_APPROCHE → DEPOSE_CLIENT)
    if (eventDerivedRange) return eventDerivedRange;
    // Priority 2: Mission active period if available
    const start = missionActiveStart ? new Date(missionActiveStart).getTime() : null;
    const end = missionActiveEnd ? new Date(missionActiveEnd).getTime() : null;
    if (start && end) return { start, end };
    if (start && !end) return { start, end: Date.now() };
    // Fallback: use data range
    if (dataRange) return { start: dataRange.start, end: dataRange.end };
    const now = new Date(); now.setHours(0, 0, 0, 0);
    return { start: now.getTime(), end: now.getTime() + 86400000 - 1 };
  }, [eventDerivedRange, missionActiveStart, missionActiveEnd, dataRange]);

  const timeRange = useMemo(() => zoomRange || dayRange, [zoomRange, dayRange]);
  const cursorTs = useMemo(() => timeRange.start + cursor * (timeRange.end - timeRange.start), [cursor, timeRange]);

  // Find interpolated position at a given timestamp — speed from real GPS source
  const findPosition = useCallback((ts) => {
    if (!pointMs.length) return null;
    // Stable travel direction: use the pre-computed forward-looking heading from
    // processGpsTrack (assignHeadings). If absent, look ahead/behind for a point far
    // enough to derive a meaningful bearing. This avoids the arrow spinning from noisy
    // sub-meter deltas between consecutive replay frames.
    const headingAt = (i) => {
      const base = points[i];
      if (!base) return null;
      if (Number.isFinite(base.heading)) return base.heading;
      for (let j = i + 1; j < points.length; j++) {
        if (haversineMeters(base, points[j]) >= 8) return computeBearing(base, points[j]);
      }
      for (let j = i - 1; j >= 0; j--) {
        if (haversineMeters(points[j], base) >= 8) return computeBearing(points[j], base);
      }
      return null;
    };
    if (ts <= pointMs[0]) return { lat: points[0].lat, lng: points[0].lng, speedKmh: speedAt(ts), heading: headingAt(0) };
    if (ts >= pointMs[pointMs.length - 1]) {
      const li = points.length - 1;
      return { lat: points[li].lat, lng: points[li].lng, speedKmh: speedAt(ts), heading: headingAt(li) };
    }
    let lo = 0, hi = pointMs.length - 1;
    while (lo < hi - 1) { const mid = (lo + hi) >> 1; if (pointMs[mid] <= ts) lo = mid; else hi = mid; }
    const ratio = pointMs[hi] > pointMs[lo] ? (ts - pointMs[lo]) / (pointMs[hi] - pointMs[lo]) : 0;
    const pos = interpolateGPS(points[lo], points[hi], ratio);
    // Real instantaneous speed resolved from raw GPS samples by timestamp.
    return { ...pos, speedKmh: speedAt(ts), heading: headingAt(lo) };
  }, [points, pointMs, speedAt]);

  // Emit position changes — guard so we only emit when the cursor TIMESTAMP
  // actually changes. Without this, identity churn of findPosition/onPositionChange
  // during the parent re-render (caused by setReplayPos) re-runs this effect and
  // re-emits, producing a render feedback loop ("Maximum update depth exceeded").
  const lastEmitTsRef = useRef(null);
  useEffect(() => {
    if (isLiveMode) { lastEmitTsRef.current = null; return; }
    if (lastEmitTsRef.current === cursorTs) return;
    lastEmitTsRef.current = cursorTs;
    const pos = findPosition(cursorTs);
    if (pos) onPositionChange?.({ ...pos, timestamp: cursorTs, timeStr: fmtMs(cursorTs) });
  }, [cursorTs, findPosition, onPositionChange, isLiveMode]);

  // Playback animation
  useEffect(() => {
    if (!playing) { if (animRef.current) cancelAnimationFrame(animRef.current); lastFrame.current = null; return; }
    const animate = (now) => {
      if (lastFrame.current) {
        const dt = now - lastFrame.current;
        const total = timeRange.end - timeRange.start;
        const advance = (dt * speed * 60) / total;
        // NB: never call another setState (setPlaying) inside this updater — doing so
        // triggers React "Maximum update depth exceeded". Just clamp; an effect below
        // stops playback once the cursor reaches the end.
        setCursor(prev => Math.min(1, prev + advance));
      }
      lastFrame.current = now;
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [playing, speed, timeRange]);

  // Stop playback when the replay reaches the end (kept out of the setCursor updater).
  useEffect(() => {
    if (playing && cursor >= 1) setPlaying(false);
  }, [playing, cursor]);

  // When entering replay mode, stop live
  useEffect(() => {
    if (playing && isLiveMode) onModeChange?.('replay');
  }, [playing, isLiveMode, onModeChange]);

  // Bar interactions
  const handleBarClick = useCallback((clientX) => {
    if (!barRef.current) return;
    const rect = barRef.current.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    setCursor(ratio);
    if (isLiveMode) onModeChange?.('replay');
  }, [isLiveMode, onModeChange]);

  const handleMouseDown = useCallback((e) => {
    setDragging(true); setPlaying(false);
    handleBarClick(e.clientX);
  }, [handleBarClick]);

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => handleBarClick(e.clientX);
    const up = () => {
      setDragging(false);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [dragging, handleBarClick]);

  // Touch support
  const handleTouchStart = useCallback((e) => {
    setDragging(true); setPlaying(false);
    handleBarClick(e.touches[0].clientX);
  }, [handleBarClick]);

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => { e.preventDefault(); handleBarClick(e.touches[0].clientX); };
    const end = () => setDragging(false);
    window.addEventListener('touchmove', move, { passive: false });
    window.addEventListener('touchend', end);
    return () => { window.removeEventListener('touchmove', move); window.removeEventListener('touchend', end); };
  }, [dragging, handleBarClick]);

  // Zoom in
  const handleZoomIn = useCallback((clickTs) => {
    if (zoom >= ZOOM_MINUTE) return;
    let ns, ne, nl;
    if (zoom === ZOOM_DAY) {
      const d = new Date(clickTs); d.setMinutes(0, 0, 0); ns = d.getTime(); ne = ns + 3600000 - 1; nl = ZOOM_HOUR;
    } else if (zoom === ZOOM_HOUR) {
      const d = new Date(clickTs); d.setMinutes(Math.floor(d.getMinutes() / 15) * 15, 0, 0); ns = d.getTime(); ne = ns + 900000 - 1; nl = ZOOM_QUARTER;
    } else if (zoom === ZOOM_QUARTER) {
      const d = new Date(clickTs); d.setSeconds(0, 0); ns = d.getTime(); ne = ns + 60000 - 1; nl = ZOOM_MINUTE;
    }
    if (ns !== undefined) {
      const nr = Math.max(0, Math.min(1, (cursorTs - ns) / (ne - ns)));
      setZoomRange({ start: ns, end: ne }); setZoom(nl); setCursor(nr);
    }
  }, [zoom, cursorTs]);

  // Zoom out
  const handleZoomOut = useCallback(() => {
    if (zoom === ZOOM_DAY) return;
    let ns, ne, nl;
    if (zoom === ZOOM_MINUTE) {
      const d = new Date(cursorTs); d.setMinutes(Math.floor(d.getMinutes() / 15) * 15, 0, 0); ns = d.getTime(); ne = ns + 900000 - 1; nl = ZOOM_QUARTER;
      setZoomRange({ start: ns, end: ne });
    } else if (zoom === ZOOM_QUARTER) {
      const d = new Date(cursorTs); d.setMinutes(0, 0, 0); ns = d.getTime(); ne = ns + 3600000 - 1; nl = ZOOM_HOUR;
      setZoomRange({ start: ns, end: ne });
    } else if (zoom === ZOOM_HOUR) {
      nl = ZOOM_DAY; ns = dayRange.start; ne = dayRange.end; setZoomRange(null);
    }
    setZoom(nl);
    setCursor(Math.max(0, Math.min(1, (cursorTs - ns) / (ne - ns))));
  }, [zoom, cursorTs, dayRange]);

  const labels = useMemo(() => generateTimeLabels(zoom, timeRange.start, timeRange.end), [zoom, timeRange]);

  // Data coverage bar
  const dataCoverage = useMemo(() => {
    if (!dataRange) return null;
    const s = Math.max(0, (dataRange.start - timeRange.start) / (timeRange.end - timeRange.start));
    const e = Math.min(1, (dataRange.end - timeRange.start) / (timeRange.end - timeRange.start));
    return { left: `${s * 100}%`, width: `${(e - s) * 100}%` };
  }, [dataRange, timeRange]);

  // Event markers on timeline
  const eventMarkers = useMemo(() => {
    if (!events?.length) return [];
    const dur = timeRange.end - timeRange.start;
    return events
      .filter(evt => String(evt.type || '').toUpperCase() !== 'MISSION_ACCEPTEE')
      .map((evt, i) => {
        const ms = new Date(evt.timestamp).getTime();
        const r = (ms - timeRange.start) / dur;
        if (r < 0 || r > 1) return null;
        return { ...evt, ratio: r, idx: i, color: getEventStatusColor(evt.type) };
      }).filter(Boolean);
  }, [events, timeRange]);

  // Timeline status portions (approche/attente/arret/client a bord/etc.)
  const statusBands = useMemo(() => {
    if (!events?.length) return [];
    const dur = timeRange.end - timeRange.start;
    if (dur <= 0) return [];

    const statusEvents = events
      .map((evt) => ({
        ...evt,
        ms: new Date(evt.timestamp).getTime(),
        color: getEventStatusColor(evt.type),
      }))
      .filter((evt) => Number.isFinite(evt.ms) && TIMELINE_STATUS_COLORS[String(evt.type || '').toUpperCase()] && String(evt.type || '').toUpperCase() !== 'MISSION_ACCEPTEE')
      .sort((a, b) => a.ms - b.ms);

    if (!statusEvents.length) return [];

    const bands = [];
    for (let i = 0; i < statusEvents.length; i++) {
      const current = statusEvents[i];
      const next = statusEvents[i + 1];
      const segStart = Math.max(current.ms, timeRange.start);
      const segEnd = Math.min(next?.ms ?? timeRange.end, timeRange.end);
      if (segEnd <= segStart) continue;
      const left = (segStart - timeRange.start) / dur;
      const width = (segEnd - segStart) / dur;
      if (width <= 0) continue;
      bands.push({
        left,
        width,
        color: current.color,
        label: current.label,
        type: current.type,
      });
    }

    return bands;
  }, [events, timeRange]);

  const statusLegend = useMemo(() => {
    const seen = new Set();
    const out = [];
    statusBands.forEach((band) => {
      const key = String(band.type || '');
      if (!key || seen.has(key)) return;
      seen.add(key);
      out.push({ type: key, color: band.color, label: band.label || key });
    });
    return out.slice(0, 6);
  }, [statusBands]);

  const cycleSpeed = () => setSpeed(prev => prev === 1 ? 2 : prev === 2 ? 4 : prev === 4 ? 8 : 1);

  if (!points.length) return null;

  return (
    <div className="bg-[#12121f] border-t border-white/5 px-3 py-2.5 select-none" data-testid="tracking-timeline">
      {/* Controls row */}
      <div className="flex items-center gap-2 mb-2">
        {/* Play/Pause */}
        <button onClick={() => { if (!playing && isLiveMode) onModeChange?.('replay'); setPlaying(!playing); }}
          className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
          style={{
            color: timelineTheme.accent,
            backgroundColor: withAlpha(timelineTheme.accent, '26'),
            border: `1px solid ${withAlpha(timelineTheme.accent, '4D')}`,
          }}
          data-testid="play-pause-btn">
          {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 ml-0.5" />}
        </button>

        {/* Skip */}
        <button onClick={() => setCursor(prev => Math.max(0, prev - 0.02))} className="text-gray-500 hover:text-white transition-colors" data-testid="skip-back-btn">
          <SkipBack className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => setCursor(prev => Math.min(1, prev + 0.02))} className="text-gray-500 hover:text-white transition-colors" data-testid="skip-forward-btn">
          <SkipForward className="w-3.5 h-3.5" />
        </button>

        {/* Speed */}
        <button onClick={cycleSpeed}
          className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-400 hover:text-white transition-colors"
          data-testid="speed-btn">
          <Gauge className="w-3 h-3 inline mr-0.5" />x{speed}
        </button>

        {/* Current time */}
        <span className="text-xs font-mono ml-1 font-bold" style={{ color: timelineTheme.accent }} data-testid="cursor-time">{fmtMs(cursorTs)}</span>

        {/* Instant / avg / max speed */}
        <span className="hidden sm:inline-flex items-center gap-1.5 ml-2 text-[10px] font-mono" data-testid="timeline-speed-stats">
          <span className="text-white font-bold">{speedAt(cursorTs)}</span>
          <span className="text-white/40">km/h</span>
          <span className="text-white/20">·</span>
          <span className="text-white/40">moy {speedStats.avg}</span>
          <span className="text-white/20">·</span>
          <span className="text-white/40">max {speedStats.max}</span>
        </span>

        {/* Mission date */}
        {missionDate && (
          <span className="text-[10px] text-gray-400 ml-1 hidden sm:inline" data-testid="mission-date">
            {missionDate}
          </span>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Zoom level */}
        <span className="text-[10px] text-gray-500 mr-1">{ZOOM_LABELS[zoom]}</span>
        <button onClick={handleZoomOut} disabled={zoom === ZOOM_DAY}
          className="text-gray-500 hover:text-white disabled:opacity-30 transition-colors" data-testid="zoom-out-btn">
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => handleZoomIn(cursorTs)} disabled={zoom >= ZOOM_MINUTE}
          className="text-gray-500 hover:text-white disabled:opacity-30 transition-colors" data-testid="zoom-in-btn">
          <ZoomIn className="w-3.5 h-3.5" />
        </button>

        {/* Live button */}
        <button onClick={() => { setPlaying(false); onModeChange?.('live'); }}
          className={`text-[10px] px-2.5 py-0.5 rounded-full border transition-colors ml-1 ${
            isLiveMode ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/10' : 'border-gray-600 text-gray-400 hover:text-white'
          }`}
          data-testid="timeline-live-btn">
          <Radio className="w-3 h-3 inline mr-0.5" />{isLiveMode ? 'LIVE' : 'Live'}
        </button>
      </div>

      {/* Status legend */}
      {statusLegend.length > 0 && (
        <div className="flex items-center gap-3 mb-2" data-testid="timeline-status-legend">
          {statusLegend.map((item) => (
            <span key={item.type} className="inline-flex items-center gap-1.5 text-[10px] text-gray-300 font-medium">
              <span className="w-3 h-3 rounded" style={{ backgroundColor: item.color }} />
              {item.label}
            </span>
          ))}
        </div>
      )}

      {/* Timeline bar — tall, event-colored */}
      <div className="relative h-12 cursor-pointer rounded-lg overflow-hidden" ref={barRef}
        onMouseDown={handleMouseDown} onTouchStart={handleTouchStart}
        data-testid="timeline-bar">
        {/* Background */}
        <div className="absolute inset-0 bg-white/[0.04] border border-white/5 rounded-lg" />

        {/* Status portions — full height, prominent colors */}
        {statusBands.map((band, idx) => (
          <div
            key={`status-band-${idx}`}
            className="absolute top-0 bottom-0"
            style={{
              left: `${band.left * 100}%`,
              width: `${band.width * 100}%`,
              backgroundColor: withAlpha(band.color, '66'),
              borderRight: `1px solid ${withAlpha(band.color, '33')}`,
            }}
            title={band.label}
            data-testid={`timeline-status-band-${idx}`}
          />
        ))}

        {/* Data coverage — subtle overlay */}
        {dataCoverage && (
          <div className="absolute top-0 bottom-0 rounded-lg"
            style={{
              backgroundColor: withAlpha(timelineTheme.accent, '0A'),
              left: dataCoverage.left,
              width: dataCoverage.width,
            }}
            data-testid="data-coverage" />
        )}

        {/* Event markers — positioned with dots and labels */}
        {eventMarkers.map((evt, i) => (
          <div key={i} className="absolute top-0 bottom-0 w-px"
            style={{ left: `${evt.ratio * 100}%`, backgroundColor: withAlpha(evt.color, '55') }}>
            <div
              className="absolute top-1 -translate-x-1/2 w-2.5 h-2.5 rounded-full border-2 border-[#12121f] shadow-sm z-10"
              style={{ backgroundColor: evt.color, boxShadow: `0 0 4px ${withAlpha(evt.color, '88')}` }}
              title={`${evt.label} — ${evt.timestamp_local || ''}`}
            />
          </div>
        ))}

        {/* Time labels */}
        {labels.map((l, i) => (
          <div key={i} className="absolute bottom-0 -translate-x-1/2 text-[8px] text-gray-500 leading-none pb-0.5 z-10"
            style={{ left: `${l.ratio * 100}%` }}>
            <div className="absolute top-0 left-1/2 w-px h-2 bg-white/8 -translate-y-full" />
            {l.label}
          </div>
        ))}

        {/* Cursor — no CSS transition for immediate drag response */}
        <div className="absolute top-0 bottom-0 w-0.5 z-20"
          style={{
            backgroundColor: timelineTheme.accent,
            left: `${cursor * 100}%`,
            boxShadow: `0 0 8px ${withAlpha(timelineTheme.accent, 'AA')}`,
            transition: dragging || playing ? 'none' : 'left 0.1s ease-out',
          }}
          data-testid="timeline-cursor">
          <div
            className="absolute -top-2 -translate-x-1/2 w-4 h-4 rounded-full border-2 border-[#0f0f1a] shadow-lg"
            style={{ backgroundColor: timelineTheme.accent, boxShadow: `0 0 10px ${withAlpha(timelineTheme.accent, '88')}` }}
          />
        </div>
      </div>

      {/* Time range indicator */}
      <div className="flex items-center justify-between mt-1 text-[9px] text-gray-600">
        <span>{fmtMs(timeRange.start)}</span>
        {missionDate && <span className="sm:hidden">{missionDate}</span>}
        <span>{fmtMs(timeRange.end)}</span>
      </div>
    </div>
  );
}

// ==========================================
// GPS SIGNAL QUALITY HEATMAP — migrated from deprecated HeatmapLayer to native Circles
// ==========================================
function GpsHeatmapLayer({ map, visible }) {
  const circlesRef = useRef([]);
  const [heatData, setHeatData] = useState([]);
  const fetchTimerRef = useRef(null);
  const listenerRef = useRef(null);

  const getHeatColor = (intensity) => {
    if (intensity < 0.2) return '#00FF00';
    if (intensity < 0.4) return '#ADFF2F';
    if (intensity < 0.6) return '#FFFF00';
    if (intensity < 0.8) return '#FFA500';
    return '#FF0000';
  };

  // Fetch heatmap data from API based on map bounds
  const fetchHeatmap = useCallback(async () => {
    if (!map) return;
    const bounds = map.getBounds();
    if (!bounds) return;
    const zoom = map.getZoom();
    if (zoom < 10) { setHeatData([]); return; }

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    try {
      const res = await fetch(
        `${API}/dispatch/gps-heatmap?min_lat=${sw.lat()}&min_lng=${sw.lng()}&max_lat=${ne.lat()}&max_lng=${ne.lng()}`
      );
      if (res.ok) {
        const data = await res.json();
        setHeatData(data.points || []);
      }
    } catch (e) {
      /* silent — heatmap is non-critical */
    }
  }, [map]);

  // Debounced fetch on map idle (pan/zoom)
  useEffect(() => {
    if (!map || !visible) return;

    const debouncedFetch = () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
      fetchTimerRef.current = setTimeout(fetchHeatmap, 500);
    };

    debouncedFetch();
    listenerRef.current = map.addListener('idle', debouncedFetch);

    return () => {
      if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
      if (listenerRef.current) window.google?.maps?.event?.removeListener(listenerRef.current);
    };
  }, [map, visible, fetchHeatmap]);

  // Draw/update/destroy circles on data change
  useEffect(() => {
    // Clear old circles
    circlesRef.current.forEach(c => c.setMap(null));
    circlesRef.current = [];

    if (!map || !visible || !window.google?.maps) return;

    circlesRef.current = heatData.map(p => {
      const intensity = p.intensity || 0;
      return new window.google.maps.Circle({
        center: { lat: p.lat, lng: p.lng },
        radius: 180,
        fillColor: getHeatColor(intensity),
        fillOpacity: 0.25 + intensity * 0.45,
        strokeOpacity: 0,
        map: map,
        clickable: false,
        zIndex: 1,
      });
    });

    return () => {
      circlesRef.current.forEach(c => c.setMap(null));
      circlesRef.current = [];
    };
  }, [map, visible, heatData]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      circlesRef.current.forEach(c => c.setMap(null));
      circlesRef.current = [];
    };
  }, []);

  return null;
}

// ==========================================
// ANIMATED CAR MARKER (imperative Google Maps, no React re-renders)
// ==========================================
function AnimatedCarMarker({ map, target, heading, isLive, mapType, driverName, replayTimeStr }) {
  const markerRef = useRef(null);
  const posRef = useRef(null);
  const headingRef = useRef(0);
  const iconBaseRef = useRef(null);

  // Create/destroy marker
  useEffect(() => {
    if (!map || !window.google) return;
    markerRef.current = new window.google.maps.Marker({ map, zIndex: 999, visible: false });
    return () => {
      if (markerRef.current) { markerRef.current.setMap(null); markerRef.current = null; }
    };
  }, [map]);

  // Update icon style when mode/map type changes
  useEffect(() => {
    if (!markerRef.current || !window.google) return;
    const icon = {
      path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
      scale: mapType !== 'roadmap' ? 5 : 3.5,
      fillColor: mapType !== 'roadmap' ? '#FFFFFF' : (isLive ? '#C9A24D' : '#F59E0B'),
      fillOpacity: 1,
      strokeColor: mapType !== 'roadmap' ? '#C9A24D' : '#fff',
      strokeWeight: mapType !== 'roadmap' ? 3 : 1.5,
      rotation: headingRef.current,
    };
    iconBaseRef.current = icon;
    markerRef.current.setIcon(icon);
    markerRef.current.setTitle(
      isLive ? (driverName || 'Chauffeur') : `Replay: ${replayTimeStr || ''}`
    );
  }, [map, isLive, mapType, driverName, replayTimeStr]);

  // Keep the latest target/heading in refs so the persistent animation loop can read
  // them WITHOUT restarting on every prop change (the per-prop restart + direct
  // setPosition jump made replay playback stutter on heavy re-render frames).
  const targetRef = useRef(null);
  const headingTargetRef = useRef(0);
  useEffect(() => { targetRef.current = target || null; }, [target]);
  useEffect(() => { if (Number.isFinite(heading)) headingTargetRef.current = heading; }, [heading]);

  // Persistent requestAnimationFrame follow loop — fully decoupled from React renders.
  // Exponential smoothing glides the marker toward the latest target each frame, which
  // hides frame drops from the heavy parent re-renders → smooth playback (no "à-coups").
  // A large jump (timeline seek/scrub) snaps instantly so the cursor stays in sync.
  useEffect(() => {
    if (!markerRef.current) return;
    let raf;
    let lastT = performance.now();
    const SNAP_DIST_M = 1500;  // timeline seek → jump instantly
    const POS_TAU_MS = 55;     // position smoothing time constant
    const HEAD_TAU_MS = 90;    // heading smoothing time constant
    const loop = (now) => {
      const dt = Math.min(120, now - lastT); lastT = now;
      const m = markerRef.current;
      const tgt = targetRef.current;
      if (m && tgt) {
        if (!posRef.current) posRef.current = { lat: tgt.lat, lng: tgt.lng };
        const dist = haversineMeters(posRef.current, tgt);
        if (dist > SNAP_DIST_M) {
          posRef.current = { lat: tgt.lat, lng: tgt.lng };
        } else if (dist > 0.2) {
          const a = 1 - Math.exp(-dt / POS_TAU_MS);
          posRef.current = {
            lat: posRef.current.lat + (tgt.lat - posRef.current.lat) * a,
            lng: posRef.current.lng + (tgt.lng - posRef.current.lng) * a,
          };
        }
        m.setVisible(true);
        m.setPosition(posRef.current);
        // Heading easing along the shortest angular path
        const ht = headingTargetRef.current || 0;
        let dh = ht - headingRef.current;
        if (dh > 180) dh -= 360;
        if (dh < -180) dh += 360;
        const ah = 1 - Math.exp(-dt / HEAD_TAU_MS);
        headingRef.current = (headingRef.current + dh * ah + 360) % 360;
        if (iconBaseRef.current) m.setIcon({ ...iconBaseRef.current, rotation: headingRef.current });
      } else if (m) {
        m.setVisible(false);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [map]);

  return null; // Marker managed imperatively
}

// ==========================================
// MAIN PAGE
// ==========================================
export default function PublicTrackingPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [liveLocation, setLiveLocation] = useState(null);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEvents, setShowEvents] = useState(true);
  const [mode, setMode] = useState('live'); // 'live' | 'replay'
  const [replayPos, setReplayPos] = useState(null);
  const [showTraffic, setShowTraffic] = useState(false);
  const [mapType, setMapType] = useState('roadmap');
  const [showHeatmap, setShowHeatmap] = useState(false);
  const [isResyncing, setIsResyncing] = useState(false);
  const [resyncResult, setResyncResult] = useState(null);
  const [heading, setHeading] = useState(0);
  const [etaInfo, setEtaInfo] = useState(null); // { distance, duration, label }
  const [snappedSegments, setSnappedSegments] = useState(null); // Snap-to-road result
  const [snapLoading, setSnapLoading] = useState(false);
  const [mapInstance, setMapInstance] = useState(null); // Google Maps instance for AnimatedCarMarker
  // Admin-only quick tools (visible only when a logged-in admin/dispatcher views this public link)
  const [videoExportOpen, setVideoExportOpen] = useState(false);
  const [analysisOpen, setAnalysisOpen] = useState(false);
  const isAdminViewer = useMemo(() => {
    try {
      if (!localStorage.getItem('access_token')) return false;
      const raw = localStorage.getItem('user');
      if (!raw) return false;
      const u = JSON.parse(raw);
      const roleNames = (u?.roles || [])
        .map(r => (typeof r === 'string' ? r : r?.name))
        .filter(Boolean)
        .map(r => String(r).toUpperCase());
      const ADMIN_ROLES = ['SUPERADMIN', 'ADMIN', 'DISPATCHER'];
      return roleNames.some(r => ADMIN_ROLES.includes(r));
    } catch { return false; }
  }, []);
  const snapCacheRef = useRef(null); // Fingerprint of GPS data used for current snap
  const snapSourceFingerprintRef = useRef(null); // Fingerprint stored when snap completes
  const lastSnapAtRef = useRef(0);
  const speedLimitCacheRef = useRef({}); // Grid key -> speed limit
  const mapRef = useRef(null);
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const lastEtaCalcRef = useRef(0);
  const headingRef = useRef(null);

  // Live smoothing pipeline (Uber-like)
  const gpsBufferRef = useRef(new GpsBuffer(SMOOTHING_CONFIG.MAX_POINTS_BUFFER));
  const [smoothedLivePath, setSmoothedLivePath] = useState([]);

  // Chat state
  const [showChat, setShowChat] = useState(true);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatSenderName, setChatSenderName] = useState(() => localStorage.getItem('ygo_chat_name') || '');
  const [nameInput, setNameInput] = useState('');
  const chatEndRef = useRef(null);
  const chatIntervalRef = useRef(null);

  const updateSmoothedHeading = useCallback((rawHeading, { blend = 0.45, maxStep = 28 } = {}) => {
    if (rawHeading == null || Number.isNaN(rawHeading)) return;
    const normalized = ((rawHeading % 360) + 360) % 360;
    if (headingRef.current == null || Number.isNaN(headingRef.current)) {
      headingRef.current = normalized;
      setHeading(normalized);
      return;
    }
    let delta = normalized - headingRef.current;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    const bounded = Math.max(-maxStep, Math.min(maxStep, delta));
    const next = (headingRef.current + bounded * blend + 360) % 360;
    headingRef.current = next;
    setHeading(next);
  }, []);

  const isLive = mode === 'live';

  // Clear smoothing buffer when switching modes
  useEffect(() => {
    if (isLive) {
      gpsBufferRef.current.clear();
      setSmoothedLivePath([]);
    }
  }, [isLive]);
  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: MAPS_KEY, libraries: LIBRARIES });

  // Mission-end detection: stop live tracking when mission is completed/cancelled
  const isMissionComplete = useMemo(() => {
    const status = data?.mission?.status;
    if (!status) return false;
    return ['completed', 'cancelled', 'closed', 'TERMINE', 'depose_client'].includes(status);
  }, [data?.mission?.status]);

  // Effective live location: null if mission ended OR if the live fix is stale.
  // A genuine live mission's last fix is the MOST RECENT point (newer than the
  // recorded trail). A historical/ended trip carries a stale fix (often from a
  // later/other mission) that is OLDER than the last trail point — ignore it so it
  // doesn't draw a false "current position" + dashed bridge far away.
  // NB: we compare against the trail's last timestamp (not Date.now()) so it works
  // even when test data lives in the future relative to the server clock.
  const effectiveLive = useMemo(() => {
    if (!liveLocation || isMissionComplete) return null;
    const lu = liveLocation.last_update ? new Date(liveLocation.last_update).getTime() : null;
    const lastTrailTs = locations.length ? new Date(locations[locations.length - 1].timestamp).getTime() : null;
    // Stale if the live fix predates the most recent recorded GPS point (minus 60s slack).
    if (lu && lastTrailTs && lu < lastTrailTs - 60000) return null;
    return liveLocation;
  }, [liveLocation, isMissionComplete, locations]);

  // Fetch initial mission data
  const fetchMissionData = useCallback(async () => {
    try {
      const res = await fetch(`${API}/public/track/${token}`);
      if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.detail || 'Lien de tracking invalide'); }
      const json = await res.json();
      setData(json.data);
      if (json.data.live_location) setLiveLocation(json.data.live_location);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [token]);

  // Fetch location history
  const fetchLocations = useCallback(async (incremental = false) => {
    try {
      let url = `${API}/public/track/${token}/locations`;
      if (incremental && lastTimestampRef.current) url += `?since=${encodeURIComponent(lastTimestampRef.current)}`;
      const res = await fetch(url);
      if (!res.ok) return;
      const json = await res.json();
      if (json.live_location) setLiveLocation(json.live_location);
      // Backend returns points in json.data (array), with recorded_at as timestamp key
      const rawPoints = Array.isArray(json.data) ? json.data : (json.data?.points || []);
      const points = rawPoints.map(p => ({
        lat: p.lat, lng: p.lng, accuracy: p.accuracy,
        timestamp: p.recorded_at || p.timestamp,
        speed: p.speed, heading: p.heading,
        source: p.source,
        ...(p.segment_start ? { segment_start: true } : {}),
      }));
      if (incremental && points.length > 0) setLocations(prev => mergeLocationPoints(prev, points));
      else if (!incremental) setLocations(mergeLocationPoints([], points));
      if (points.length > 0) lastTimestampRef.current = points[points.length - 1].timestamp;
    } catch { /* silent */ }
  }, [token]);

  // Fetch events
  const fetchEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API}/public/track/${token}/events`);
      if (!res.ok) return;
      const json = await res.json();
      setEvents(json.data || []);
    } catch { /* silent */ }
  }, [token]);

  // Initial load
  useEffect(() => { fetchMissionData(); fetchLocations(false); fetchEvents(); }, [fetchMissionData, fetchLocations, fetchEvents]);

  // Chat: fetch messages
  const fetchChatMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API}/public/track/${token}/chat`);
      if (!res.ok) return;
      const json = await res.json();
      setChatMessages(json.messages || []);
    } catch { /* silent */ }
  }, [token]);

  // Chat: auto-scroll to bottom
  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Chat: poll every 5s
  useEffect(() => {
    fetchChatMessages();
    chatIntervalRef.current = setInterval(fetchChatMessages, 5000);
    return () => clearInterval(chatIntervalRef.current);
  }, [fetchChatMessages]);

  // Chat: send message
  const handleSendChat = async () => {
    if (!chatInput.trim() || !chatSenderName) return;
    setChatSending(true);
    try {
      const res = await fetch(`${API}/public/track/${token}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: chatInput.trim(), sender_name: chatSenderName }),
      });
      if (res.ok) {
        setChatInput('');
        await fetchChatMessages();
      } else {
        const err = await res.json().catch(() => ({}));
        alert(err.detail || 'Erreur lors de l\'envoi');
      }
    } catch { alert('Erreur réseau'); }
    setChatSending(false);
  };

  // Chat: save name
  const handleSaveName = () => {
    const name = nameInput.trim();
    if (!name) return;
    setChatSenderName(name);
    localStorage.setItem('ygo_chat_name', name);
    setNameInput('');
  };

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!isLive || error || isMissionComplete) return;

    const connectWs = () => {
      // Build WS URL from config.API_URL (supports both relative '' and absolute 'https://...')
      let wsUrl;
      const cfgUrl = config.API_URL;
      if (cfgUrl && cfgUrl.startsWith('http')) {
        const parsed = new URL(cfgUrl);
        const wsProto = parsed.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${wsProto}//${parsed.host}/api/public/track/${token}/ws`;
      } else {
        const wsProto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${wsProto}//${window.location.host}/api/public/track/${token}/ws`;
      }
      try {
        const ws = new WebSocket(wsUrl);

        ws.onmessage = (event) => {
          try {
            const msg = JSON.parse(event.data);
            if (msg.type === 'location_update' && msg.data) {
              const { lat, lng, heading: wsHeading, accuracy, speed, segment_start } = msg.data;
              if (lat == null || lng == null) return;
              const nextPos = { lat, lng };

              // Feed into smoothing buffer
              const accepted = gpsBufferRef.current.addPoint({ lat, lng, accuracy, speed });
              if (accepted) {
                setSmoothedLivePath([...gpsBufferRef.current.getSmoothedPath()]);
              }

              setLiveLocation(prev => {
                if (prev) {
                  const movementM = haversineMeters(prev, nextPos);
                  if (movementM >= 6) {
                    updateSmoothedHeading(wsHeading ?? computeBearing(prev, nextPos), { blend: 0.6, maxStep: 26 });
                  } else if (wsHeading != null && (speed ?? 0) > 2) {
                    updateSmoothedHeading(wsHeading, { blend: 0.3, maxStep: 14 });
                  }
                } else if (wsHeading != null) {
                  updateSmoothedHeading(wsHeading, { blend: 1, maxStep: 360 });
                }
                return { lat, lng, last_update: msg.timestamp, accuracy };
              });

              // FT-5: Add point to locations trail with segment_start flag
              const now = msg.timestamp || new Date().toISOString();
              setLocations(prev => {
                const last = prev[prev.length - 1];
                if (last && Math.abs(last.lat - lat) < 0.00001 && Math.abs(last.lng - lng) < 0.00001) return prev;
                const pt = { lat, lng, accuracy, timestamp: now, speed, heading: wsHeading, source: 'ws-live' };
                if (segment_start) pt.segment_start = true;
                return mergeLocationPoints(prev, [pt]);
              });
              lastTimestampRef.current = now;
            }
          } catch { /* ignore parse errors */ }
        };

        ws.onclose = () => {
          wsRef.current = null;
          // Reconnect after delay
          wsReconnectRef.current = setTimeout(connectWs, WS_RECONNECT_DELAY);
        };

        ws.onerror = () => {};
        wsRef.current = ws;
      } catch { /* WS optional */ }
    };

    connectWs();

    // Refresh events + periodic incremental catch-up (anti trous)
    const eventsInterval = setInterval(fetchEvents, 10000);
    const catchupInterval = setInterval(() => {
      fetchLocations(true);
    }, 12000);

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (wsReconnectRef.current) clearTimeout(wsReconnectRef.current);
      clearInterval(eventsInterval);
      clearInterval(catchupInterval);
    };
  }, [isLive, error, token, fetchEvents, fetchLocations, updateSmoothedHeading, isMissionComplete]);

  // Ticket 7.1: Center map on live location — gentle pan WITHOUT zoom change
  const lastPanRef = useRef(null);
  useEffect(() => {
    if (!mapRef.current || !effectiveLive || !isLive) return;
    const newPos = { lat: effectiveLive.lat, lng: effectiveLive.lng };
    // Only pan if position changed significantly (avoid jitter)
    if (lastPanRef.current) {
      const dlat = Math.abs(newPos.lat - lastPanRef.current.lat);
      const dlng = Math.abs(newPos.lng - lastPanRef.current.lng);
      if (dlat < 0.0001 && dlng < 0.0001) return; // Skip tiny movements
    }
    lastPanRef.current = newPos;
    // panTo smoothly WITHOUT changing zoom level
    mapRef.current.panTo(newPos);
  }, [effectiveLive, isLive]);

  // ETA computation: use Google DistanceMatrixService, throttled to max every 30s
  useEffect(() => {
    if (!window.google || !effectiveLive || !data?.mission || !isLive) return;
    const now = Date.now();
    if (now - lastEtaCalcRef.current < 30000) return; // Throttle: 30s

    const mission = data.mission;
    const status = mission.status || '';

    // Determine if driver is before or after pickup
    const prePickupStatuses = ['assigned', 'accepted', 'en_route', 'waiting_for_pickup', 'en_approche', 'en_attente', 'en_attente_pec', 'on_site', 'proposed', 'CONFIRMED', 'confirmed'];
    const isBeforePickup = prePickupStatuses.includes(status);
    const destinationAddress = isBeforePickup ? mission.pickup_address : mission.dropoff_address;

    if (!destinationAddress) return;

    lastEtaCalcRef.current = now;

    const service = new window.google.maps.DistanceMatrixService();
    service.getDistanceMatrix({
      origins: [{ lat: effectiveLive.lat, lng: effectiveLive.lng }],
      destinations: [destinationAddress],
      travelMode: window.google.maps.TravelMode.DRIVING,
      drivingOptions: { departureTime: new Date(), trafficModel: 'bestGuess' },
      unitSystem: window.google.maps.UnitSystem.METRIC,
    }, (response, responseStatus) => {
      if (responseStatus === 'OK' && response.rows?.[0]?.elements?.[0]?.status === 'OK') {
        const el = response.rows[0].elements[0];
        const durationInTraffic = el.duration_in_traffic || el.duration;
        setEtaInfo({
          distance: el.distance?.text || '',
          duration: durationInTraffic?.text || '',
          durationValue: durationInTraffic?.value || 0,
          label: isBeforePickup ? 'Arrivée prise en charge' : 'Arrivée destination',
          destination: destinationAddress,
        });
      }
    });
  }, [effectiveLive, data, isLive]);

  // Handle replay position from timeline
  const lastReplayPosRef = useRef(null);
  const lastReplayPanRef = useRef(0);
  const handleReplayPosition = useCallback((pos) => {
    // Compute heading OUTSIDE any setState updater (calling setHeading inside the
    // setReplayPos updater triggers React "Maximum update depth exceeded").
    const prev = lastReplayPosRef.current;
    if (pos && Number.isFinite(pos.heading)) {
      // Stable forward-looking heading from processGpsTrack — points toward the next
      // GPS point. Immune to noisy sub-meter deltas (which made the arrow spin).
      updateSmoothedHeading(pos.heading, { blend: 0.6, maxStep: 30 });
    } else if (prev && pos && haversineMeters(prev, pos) >= 8) {
      // Fallback only when no precomputed heading: derive from a meaningful movement delta.
      updateSmoothedHeading(computeBearing(prev, pos), { blend: 0.55, maxStep: 30 });
    }
    lastReplayPosRef.current = pos;
    setReplayPos(pos);
    // Throttle map re-centering (panning every emit fought with the smooth marker glide
    // and made the map background jerk). Pan at most ~4x/sec; the marker itself stays
    // smooth via its own rAF loop.
    if (mapRef.current && pos) {
      const nowMs = performance.now();
      if (nowMs - lastReplayPanRef.current > 250) {
        lastReplayPanRef.current = nowMs;
        mapRef.current.panTo({ lat: pos.lat, lng: pos.lng });
      }
    }
  }, [updateSmoothedHeading]);

  // Handle mode change from timeline
  const handleModeChange = useCallback((newMode) => {
    // Don't allow live mode if mission is complete
    if (newMode === 'live' && isMissionComplete) return;
    setMode(newMode);
    if (newMode === 'live') { setReplayPos(null); }
  }, [isMissionComplete]);

  // Auto-switch to replay when mission ends
  useEffect(() => {
    if (isMissionComplete && mode === 'live') {
      setMode('replay');
    }
  }, [isMissionComplete, mode]);

  // Fit map bounds to show all GPS points
  const fitBounds = useCallback(() => {
    if (!mapRef.current || !window.google || locations.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    locations.forEach(p => bounds.extend({ lat: p.lat, lng: p.lng }));
    if (effectiveLive) bounds.extend({ lat: effectiveLive.lat, lng: effectiveLive.lng });
    mapRef.current.fitBounds(bounds, { top: 50, right: 50, bottom: 80, left: 50 });
  }, [locations, effectiveLive]);

  // Resync GPS — re-snap all points to rebuild polyline
  const handleResync = useCallback(async () => {
    setIsResyncing(true);
    setResyncResult(null);
    try {
      const res = await fetch(`${API}/public/track/${token}/resync`, { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setResyncResult({ ok: true, msg: `${data.snapped} pts re-snappés` });
        // Reload locations after short delay to let snapped data propagate
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setResyncResult({ ok: false, msg: data.detail || 'Erreur' });
      }
    } catch (err) {
      setResyncResult({ ok: false, msg: 'Erreur réseau' });
    }
    setIsResyncing(false);
  }, [token]);

  // Auto-fit on first location load
  const hasFittedRef = useRef(false);
  useEffect(() => {
    if (locations.length > 1 && !hasFittedRef.current && mapRef.current && window.google) {
      hasFittedRef.current = true;
      setTimeout(fitBounds, 500);
    }
  }, [locations, fitBounds]);

  // Determine the active marker position
  const activePosition = useMemo(() => {
    if (isLive && effectiveLive) return { lat: effectiveLive.lat, lng: effectiveLive.lng };
    if (replayPos) return { lat: replayPos.lat, lng: replayPos.lng };
    if (effectiveLive) return { lat: effectiveLive.lat, lng: effectiveLive.lng };
    if (locations.length > 0) return { lat: locations[locations.length - 1].lat, lng: locations[locations.length - 1].lng };
    return null;
  }, [isLive, effectiveLive, replayPos, locations]);

  // GPS Processing Pipeline v5: UNIFIED — always process ALL points once (same for live + replay)
  // FIX #2+#3: Single data source + single pipeline run, no mode differentiation
  const pathSegments = useMemo(() => {
    let pts = [...locations];
    // Drop stale "live" fixes that are temporally disconnected from the recorded trail.
    // A current-position point can arrive via WebSocket for an already-finished trip
    // (mode starts 'live' before the mission is known complete). Such a point sits
    // hours/days after the last real GPS sample and would otherwise stretch the replay
    // interpolation range, freezing the marker near the start. Real signal losses are
    // only minutes long, so a >1h trailing gap is always a stale live fix — never trip data.
    if (pts.length > 2) {
      pts.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      const STALE_GAP_MS = 60 * 60 * 1000; // 1h
      while (pts.length > 2) {
        const last = new Date(pts[pts.length - 1].timestamp).getTime();
        const prev = new Date(pts[pts.length - 2].timestamp).getTime();
        if (Number.isFinite(last) && Number.isFinite(prev) && (last - prev) > STALE_GAP_MS) {
          pts.pop();
        } else break;
      }
    }
    if (effectiveLive) {
      // Only bridge to live position when it's temporally + spatially close to the last GPS point.
      // This prevents a fake straight dashed line when there's a large gap between the last
      // stored GPS point and the current live position (e.g., en_approche → current position).
      const lastLoc = locations.length > 0 ? locations[locations.length - 1] : null;
      const shouldBridge = (() => {
        if (!lastLoc) return true; // No history yet — include live as first point
        const liveTs = effectiveLive.last_update
          ? new Date(effectiveLive.last_update).getTime()
          : Date.now();
        const lastTs = new Date(lastLoc.timestamp || 0).getTime();
        const timeDiffMs = Math.abs(liveTs - lastTs);
        const distM = haversineMeters(lastLoc, effectiveLive);
        // Only add to trail if within 10 min AND 10 km of last GPS point
        return timeDiffMs < 600_000 && distM < 10_000;
      })();

      if (shouldBridge) {
        pts.push({
          lat: effectiveLive.lat,
          lng: effectiveLive.lng,
          accuracy: effectiveLive.accuracy,
          timestamp: effectiveLive.last_update || new Date().toISOString(),
        });
      }
    }
    if (pts.length === 0) return [];
    return processGpsTrack(pts);
  }, [locations, effectiveLive]);

  // Snap-to-road: ZERO API CALLS — local Catmull-Rom smoothing only
  // Server-side snap data used when available, otherwise local smoothing
  useEffect(() => {
    if (!pathSegments.length) return;
    if (isLive) return; // Live uses GpsBuffer pipeline

    const segsToSnap = pathSegments.filter(s => s.length >= 2);
    if (!segsToSnap.length) return;

    const fingerprint = computeSegmentsFingerprint(pathSegments);
    if (snapCacheRef.current === fingerprint) return;

    // Local Catmull-Rom smoothing (zero API cost)
    try {
      const smoothedResults = pathSegments.map(seg => {
        if (seg.length < 3) return seg;

        // Filter: remove too-close points AND detect huge jumps (outliers)
        const MAX_JUMP_M = 1000; // 1km max between consecutive points
        const filtered = [];
        let last = null;
        for (const p of seg) {
          if (last) {
            const d = Math.sqrt(Math.pow(p.lat - last.lat, 2) + Math.pow(p.lng - last.lng, 2)) * 111000;
            if (d < 5) continue;      // too close
            if (d > MAX_JUMP_M) continue; // outlier jump — skip this point entirely
          }
          filtered.push(p);
          last = p;
        }
        // If after filtering we lost too many points, return raw segment
        if (filtered.length < 3) return seg;

        // Catmull-Rom — only smooth between nearby points
        const smooth = [];
        const tension = 0.5, subdivs = 6;
        for (let i = 0; i < filtered.length - 1; i++) {
          const p1 = filtered[i];
          const p2 = filtered[i + 1];
          // Check distance between p1 and p2 — if too far, just add straight without subdivision
          const segDist = Math.sqrt(Math.pow(p2.lat - p1.lat, 2) + Math.pow(p2.lng - p1.lng, 2)) * 111000;
          if (segDist > 500) {
            // Large gap: just connect straight, no spline
            smooth.push({ ...p1 });
            continue;
          }
          const p0 = filtered[Math.max(0, i - 1)];
          const p3 = filtered[Math.min(filtered.length - 1, i + 2)];
          smooth.push({ ...p1 });
          // Real per-interval timing: subdivision points get timestamps interpolated
          // in TIME between p1 and p2 (their real GPS timestamps), NOT by cumulative
          // distance. This keeps the replay marker chronologically accurate — at stops
          // (long time gap, tiny distance) the marker dwells instead of racing ahead.
          const t1ms = new Date(p1.timestamp).getTime();
          const t2ms = new Date(p2.timestamp).getTime();
          const hasTime = Number.isFinite(t1ms) && Number.isFinite(t2ms) && t2ms > t1ms;
          for (let j = 1; j < subdivs; j++) {
            const t = j / subdivs, t2 = t * t, t3 = t2 * t, s = (1 - tension) / 2;
            // Correct cardinal spline basis — ensures P(0)=P1, P(1)=P2 for any tension
            const h1 = -s * t3 + 2 * s * t2 - s * t;
            const h2 = (2 - s) * t3 + (s - 3) * t2 + 1;
            const h3 = (s - 2) * t3 + (3 - 2 * s) * t2 + s * t;
            const h4 = s * t3 - s * t2;
            smooth.push({
              lat: h1 * p0.lat + h2 * p1.lat + h3 * p2.lat + h4 * p3.lat,
              lng: h1 * p0.lng + h2 * p1.lng + h3 * p2.lng + h4 * p3.lng,
              timestamp: hasTime ? new Date(t1ms + (t2ms - t1ms) * t).toISOString() : p1.timestamp,
            });
          }
        }
        smooth.push({ ...filtered[filtered.length - 1] });
        // Keep the real per-interval timestamps computed above (do NOT redistribute by
        // distance — that desynchronises the cursor from the mission chronology).
        return smooth;
      });

      snapCacheRef.current = fingerprint;
      snapSourceFingerprintRef.current = fingerprint;
      setSnappedSegments(smoothedResults);
    } catch {
      // Smoothing failed — keep raw
    }
  }, [pathSegments, isLive]);

  // Speed limits: fetch for sampled route points when entering replay
  useEffect(() => {
    if (isLive || !pathSegments.length) return;
    // Already fetched?
    if (Object.keys(speedLimitCacheRef.current).length > 0) return;

    const allPts = pathSegments.flat();
    if (allPts.length < 2) return;

    // Sample every Nth point (max 100 unique grid cells)
    const step = Math.max(1, Math.floor(allPts.length / 100));
    const sampled = [];
    const seen = new Set();
    for (let i = 0; i < allPts.length; i += step) {
      const gk = `${Math.round(allPts[i].lat * 1000) / 1000},${Math.round(allPts[i].lng * 1000) / 1000}`;
      if (!seen.has(gk)) {
        seen.add(gk);
        sampled.push({ lat: allPts[i].lat, lng: allPts[i].lng });
      }
    }
    if (!sampled.length) return;

    let cancelled = false;
    const fetchLimits = async () => {
      try {
        const res = await fetch(`${API}/tracking/speed-limits/batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ points: sampled }),
        });
        if (res.ok && !cancelled) {
          const json = await res.json();
          speedLimitCacheRef.current = json.speed_limits || {};
        }
      } catch (e) {
        // Non-blocking
      }
    };
    fetchLimits();
    return () => { cancelled = true; };
  }, [pathSegments, isLive]);

  // Use snapped segments ONLY when they match the current GPS data.
  // If the snap is stale (fingerprint mismatch), fallback immediately to raw GPS
  // to preserve visual fluidity — no "ligne en retard" or straight-line artifacts.
  const displaySegments = useMemo(() => {
    if (!snappedSegments) return pathSegments;
    const currentFingerprint = computeSegmentsFingerprint(pathSegments);
    if (snapSourceFingerprintRef.current === currentFingerprint) {
      return snappedSegments;
    }
    // Mismatch: GPS data has changed since the snap was computed.
    // Fallback to raw GPS immediately for continuity.
    return pathSegments;
  }, [snappedSegments, pathSegments]);

  // Visual separation: real connectivity gaps vs continuous route
  // v7: Only flag truly massive gaps (>5min OR >5km) — smaller gaps are merged in pipeline
  const realGapBridges = useMemo(() => {
    if (!displaySegments || displaySegments.length < 2) return [];
    const bridges = [];
    for (let i = 1; i < displaySegments.length; i++) {
      const prevSeg = displaySegments[i - 1];
      const nextSeg = displaySegments[i];
      if (!prevSeg?.length || !nextSeg?.length) continue;
      const from = prevSeg[prevSeg.length - 1];
      const to = nextSeg[0];
      const dtMs = Math.max(0, toTimestampMs(to?.timestamp) - toTimestampMs(from?.timestamp));
      const distM = haversineMeters(from, to);
      // A real signal loss = a genuine TIME gap. A large distance covered in a short
      // time is just sparse highway sampling / a GPS spike, not a coupure — don't dash it.
      const isRealGap = dtMs > 90000 || (distM > 2000 && dtMs > 20000);
      if (isRealGap) {
        bridges.push({ from, to, dtMs, distM });
      }
    }
    return bridges;
  }, [displaySegments]);

  // Helper: trim a single merged path at cutoff timestamp, return {lat, lng} points
  const trimPathToTimestamp = (path, cutoffTs) => {
    if (!path || !path.length) return [];
    const cutoffMs = typeof cutoffTs === 'number' ? cutoffTs : toTimestampMs(cutoffTs);
    if (!cutoffMs) return path.map(p => ({ lat: p.lat, lng: p.lng }));
    
    // Find the time range of this path
    let firstTs = 0, lastTs = 0;
    for (const p of path) {
      const t = p.timestamp ? toTimestampMs(p.timestamp) : 0;
      if (t > 0) {
        if (!firstTs) firstTs = t;
        lastTs = t;
      }
    }
    
    // If path has no timestamps at all, show it fully (can't trim)
    if (!firstTs) return path.map(p => ({ lat: p.lat, lng: p.lng }));
    
    // If cutoff is after the entire path, show it fully
    if (cutoffMs >= lastTs) return path.map(p => ({ lat: p.lat, lng: p.lng }));
    
    // If cutoff is before the entire path, hide it
    if (cutoffMs < firstTs) return [];
    
    // Cutoff is within the path — trim at the exact point
    const result = [];
    for (let i = 0; i < path.length; i++) {
      const t = path[i].timestamp ? toTimestampMs(path[i].timestamp) : 0;
      if (t && t > cutoffMs) {
        // Interpolate to exact cutoff position
        if (result.length > 0 && i > 0) {
          const prev = path[i - 1];
          const tPrev = prev.timestamp ? toTimestampMs(prev.timestamp) : 0;
          if (tPrev && tPrev <= cutoffMs && t > tPrev) {
            const ratio = (cutoffMs - tPrev) / (t - tPrev);
            result.push({
              lat: prev.lat + (path[i].lat - prev.lat) * ratio,
              lng: prev.lng + (path[i].lng - prev.lng) * ratio,
            });
          }
        }
        break;
      }
      result.push({ lat: path[i].lat, lng: path[i].lng });
    }
    return result;
  };


  // v7: Merge close display segments into unified polyline paths for seamless rendering
  // Compute merged paths WITH timestamps first (for cursor), then strip for polyline rendering
  const mergedPathsWithTimestamps = useMemo(() => {
    const segs = displaySegments;
    if (!segs || segs.length === 0) return [];
    if (segs.length === 1) {
      return [segs[0].map(p => ({ lat: p.lat, lng: p.lng, timestamp: p.timestamp }))];
    }

    // Keep timestamps during merge for gap detection and cursor positioning
    const merged = [[...segs[0]]];

    for (let i = 1; i < segs.length; i++) {
      const prev = merged[merged.length - 1];
      const curr = segs[i];
      if (!prev.length || !curr.length) {
        merged.push([...curr]);
        continue;
      }

      const lastPt = prev[prev.length - 1];
      const firstPt = curr[0];
      const dtMs = toTimestampMs(firstPt?.timestamp) - toTimestampMs(lastPt?.timestamp);
      const distM = haversineMeters(lastPt, firstPt);

      // Connect with solid line only if gap ≤ 90s AND ≤ 2km — match realGapBridges threshold
      if (dtMs >= 0 && dtMs <= 90000 && distM <= 2000) {
        // Smooth bridge — add intermediate points with interpolated timestamps
        if (distM > 50) {
          const steps = Math.max(2, Math.min(8, Math.ceil(distM / 500)));
          const tStart = toTimestampMs(lastPt.timestamp);
          const tEnd = toTimestampMs(firstPt.timestamp);
          for (let s = 1; s < steps; s++) {
            const r = s / steps;
            prev.push({
              lat: lastPt.lat + (firstPt.lat - lastPt.lat) * r,
              lng: lastPt.lng + (firstPt.lng - lastPt.lng) * r,
              timestamp: tStart && tEnd ? new Date(tStart + r * (tEnd - tStart)).toISOString() : firstPt.timestamp,
            });
          }
        }
        // Append current segment points
        for (const pt of curr) prev.push(pt);
      } else {
        merged.push([...curr]);
      }
    }

    return merged;
  }, [displaySegments]);

  const mergedPolylinePaths = useMemo(() => {
    if (!mergedPathsWithTimestamps.length) return [];

    // Trim merged paths for replay mode (trim AFTER merge to preserve gap bridges)
    if (!isLive && replayPos) {
      return mergedPathsWithTimestamps
        .map(seg => trimPathToTimestamp(seg, replayPos.timestamp))
        .filter(seg => seg.length > 0);
    }

    // Strip to {lat, lng} for rendering
    return mergedPathsWithTimestamps.map(seg => seg.map(p => ({ lat: p.lat, lng: p.lng })));
  }, [mergedPathsWithTimestamps, isLive, replayPos]);

  // Cursor points: use the MERGED polyline data (same as what's rendered) so cursor follows the polyline exactly
  // Cursor points: use the processed GPS path (pathSegments) which PRESERVES the real
  // GPS timestamps end-to-end (interpolate → kalman → simplify → merge all keep real time).
  // The snapped/merged visual polyline rebuilds timestamps and loses chronological accuracy,
  // which desynchronised the marker (it raced ahead during stops). pathSegments is smoothed
  // so the marker still sits on/near the road, but now it is time-accurate.
  const cursorPoints = useMemo(() => {
    return pathSegments
      .flatMap(seg => seg)
      .filter(p => p && p.timestamp)
      .sort((a, b) => toTimestampMs(a.timestamp) - toTimestampMs(b.timestamp));
  }, [pathSegments]);

  // ── Speed-coloured polyline (excess vs road limit) ──────────────────────
  // Fetched from the same backend logic as the video export (Valhalla limits).
  const [speedTraceSegments, setSpeedTraceSegments] = useState([]);
  useEffect(() => {
    let cancelled = false;
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API}/public/track/${token}/speed-trace`);
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled) setSpeedTraceSegments(Array.isArray(json.segments) ? json.segments : []);
      } catch { /* non-blocking */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // T16: Build event markers for events with GPS coordinates
  const eventMarkers = useMemo(() => {
    if (!events || !events.length) return [];
    return events
      .filter(e => e.lat && e.lng)
      .map((e, i) => ({
        id: i,
        lat: e.lat,
        lng: e.lng,
        label: e.label || e.type,
        timestamp_local: e.timestamp_local,
        type: e.type,
      }));
  }, [events]);

  const mapCenter = activePosition || { lat: 43.7, lng: 7.27 };

  // Compute mission date for header display (must be before early returns)
  const headerDate = useMemo(() => {
    const pickupDt = data?.mission?.pickup_datetime;
    if (pickupDt) {
      try {
        return new Date(pickupDt).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });
      } catch { /* ignore */ }
    }
    if (locations.length > 0 && locations[0].timestamp) {
      try {
        return new Date(locations[0].timestamp).toLocaleDateString('fr-FR', { weekday: 'short', day: '2-digit', month: 'long', year: 'numeric' });
      } catch { /* ignore */ }
    }
    return null;
  }, [data?.mission?.pickup_datetime, locations]);

  // Error state
  if (error) {
    const isRevoked = error.includes('révoqué');
    return (
      <div className="min-h-screen bg-[#0f0f1a] flex items-center justify-center" data-testid="tracking-error">
        <div className="text-center p-8 max-w-md">
          {isRevoked ? (
            <ShieldOff className="w-16 h-16 text-amber-400 mx-auto mb-4" />
          ) : (
            <AlertTriangle className="w-16 h-16 text-red-400 mx-auto mb-4" />
          )}
          <h1 className="text-2xl font-bold text-white mb-2">
            {isRevoked ? 'Lien désactivé' : 'Lien invalide'}
          </h1>
          <p className="text-gray-400">
            {isRevoked
              ? 'Ce lien de suivi a été désactivé par l\'administrateur.'
              : 'Ce lien de suivi est invalide ou n\'existe plus.'}
          </p>
        </div>
      </div>
    );
  }

  if (loading || !isLoaded) {
    return (
      <div className="min-h-screen bg-[#0f0f1a] flex items-center justify-center" data-testid="tracking-loading">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Chargement du suivi...</p>
        </div>
      </div>
    );
  }

  const mission = data?.mission || {};
  const driver = data?.driver || {};
  const statusCfg = STATUS_CONFIG[mission.status] || { label: mission.status, color: '#6B7280' };
  const routeColor = '#C9A24D';

  return (
    <div className="h-screen w-screen bg-[#0f0f1a] flex flex-col overflow-hidden" data-testid="public-tracking-page">
      {/* Header */}
      <div className="bg-[#12121f] border-b border-white/5 px-4 py-2.5 flex items-center justify-between shrink-0 z-10">
        <div className="flex items-center gap-3">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
            <Navigation className="w-3.5 h-3.5 text-white" />
          </div>
          <div>
            <h1 className="text-white font-semibold text-sm" data-testid="tracking-title">Y GO — Suivi en direct</h1>
            <p className="text-gray-500 text-[10px]">
              {data?.mission_type === 'EXCURSION' ? 'Excursion' : 'Transfert'} {data?.mission_ref && `#${data.mission_ref}`}
              {headerDate && <span className="text-gray-400 ml-1.5" data-testid="header-date">— {headerDate}</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {effectiveLive && isLive && (
            <div className={`flex items-center gap-1.5 rounded-full px-2.5 py-0.5 border ${
              effectiveLive.accuracy && effectiveLive.accuracy > 200
                ? 'bg-orange-500/10 border-orange-500/20'
                : 'bg-emerald-500/10 border-emerald-500/20'
            }`} data-testid="live-indicator">
              <Radio className={`w-2.5 h-2.5 animate-pulse ${
                effectiveLive.accuracy && effectiveLive.accuracy > 200 ? 'text-orange-400' : 'text-emerald-400'
              }`} />
              <span className={`text-[10px] font-medium ${
                effectiveLive.accuracy && effectiveLive.accuracy > 200 ? 'text-orange-400' : 'text-emerald-400'
              }`}>
                {effectiveLive.accuracy && effectiveLive.accuracy > 200 ? 'SIGNAL FAIBLE' : 'EN DIRECT'}
              </span>
            </div>
          )}
          {!isLive && replayPos && (() => {
            const speedKmh = replayPos.speedKmh || 0;
            const gk = `${Math.round(replayPos.lat * 1000) / 1000},${Math.round(replayPos.lng * 1000) / 1000}`;
            const limit = speedLimitCacheRef.current[gk];
            let speedColor = 'text-emerald-400'; // green = within limit
            let speedBg = 'bg-emerald-500/15';
            if (limit && speedKmh > limit + 10) {
              speedColor = 'text-red-400'; speedBg = 'bg-red-500/15';
            } else if (limit && speedKmh > limit) {
              speedColor = 'text-orange-400'; speedBg = 'bg-orange-500/15';
            }
            return (
              <div className="flex items-center gap-1.5" data-testid="replay-indicator">
                <div className="flex items-center gap-1.5 bg-amber-500/10 border border-amber-500/20 rounded-full px-2.5 py-0.5">
                  <Eye className="w-2.5 h-2.5 text-amber-400" />
                  <span className="text-amber-400 text-[10px] font-medium">REPLAY {replayPos.timeStr}</span>
                </div>
                {speedKmh > 0 && (
                  <div className={`flex items-center gap-1 ${speedBg} border ${
                    speedColor === 'text-red-400' ? 'border-red-500/20' : speedColor === 'text-orange-400' ? 'border-orange-500/20' : 'border-emerald-500/20'
                  } rounded-full px-2 py-0.5`} data-testid="replay-speed">
                    <Gauge className={`w-2.5 h-2.5 ${speedColor}`} />
                    <span className={`text-[10px] font-bold ${speedColor}`}>{speedKmh}</span>
                    <span className={`text-[9px] ${speedColor} opacity-70`}>km/h</span>
                    {limit && <span className="text-[9px] text-gray-500 ml-0.5">/{limit}</span>}
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden relative">
        {/* Map + Timeline */}
        <div className="flex-1 flex flex-col relative">
          {/* Map */}
          <div className="flex-1 relative" data-testid="tracking-map-container">
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '100%' }}
              center={mapCenter}
              zoom={13}
              options={{ styles: mapType === 'roadmap' ? mapStyles : [], disableDefaultUI: true, zoomControl: true, fullscreenControl: true, mapTypeControl: false, mapTypeId: mapType }}
              onLoad={map => { mapRef.current = map; setMapInstance(map); }}
            >
              {/* Route polyline — speed-coloured by excess vs limit (green/orange/red) */}
              {speedTraceSegments.length > 0 ? (
                speedTraceSegments.map((seg, idx) => (
                  seg.path && seg.path.length > 1 && (
                    <Polyline
                      key={`speed-seg-${idx}`}
                      path={seg.path.map(([lat, lng]) => ({ lat, lng }))}
                      options={{
                        strokeColor: SPEED_TRACE_COLORS[seg.color] || '#10B981',
                        strokeOpacity: 0.95,
                        strokeWeight: 4.5,
                        geodesic: false,
                        clickable: false,
                      }}
                    />
                  )
                ))
              ) : (
                mergedPolylinePaths.map((path, idx) => (
                  path.length > 1 && (
                    <Polyline
                      key={`route-${idx}`}
                      path={path}
                      options={{
                        strokeColor: routeColor,
                        strokeOpacity: 0.95,
                        strokeWeight: 4.5,
                        geodesic: false,
                        clickable: false,
                      }}
                    />
                  )
                ))
              )}

              {/* Smoothed live trail (Uber-like) — only in live mode */}
              {isLive && smoothedLivePath.length > 1 && (
                <Polyline
                  path={smoothedLivePath.map(p => ({ lat: p.lat, lng: p.lng }))}
                  options={{
                    strokeColor: '#C9A24D',
                    strokeOpacity: 0.9,
                    strokeWeight: 5,
                    geodesic: false,
                    clickable: false,
                  }}
                />
              )}

              {/* Inferred GPS-gap segments are intentionally NOT drawn (no dashed bridge,
                  no reprise markers) — per request the polyline simply leaves a visual gap. */}
              {snapLoading && (
                <Polyline path={[]} options={{}} />
              )}

              {/* Active marker — animated car with smooth movement and heading */}
              <AnimatedCarMarker
                map={mapInstance}
                target={activePosition}
                heading={heading}
                isLive={isLive}
                mapType={mapType}
                driverName={driver.name}
                replayTimeStr={replayPos?.timeStr}
              />

              {/* Start point */}
              {locations.length > 0 && (
                <Marker
                  position={{ lat: locations[0].lat, lng: locations[0].lng }}
                  icon={{
                    path: window.google?.maps?.SymbolPath?.CIRCLE,
                    scale: mapType !== 'roadmap' ? 8 : 6,
                    fillColor: mapType !== 'roadmap' ? '#FFFFFF' : '#10B981',
                    fillOpacity: 1,
                    strokeColor: mapType !== 'roadmap' ? '#10B981' : '#fff',
                    strokeWeight: mapType !== 'roadmap' ? 3 : 2,
                  }}
                  title="Départ"
                />
              )}

              {/* T16: Event markers on map */}
              {eventMarkers.map((em) => {
                const evtColor = em.type === 'ARRET' || em.type === 'ARRET_AUTO_DETECTE' ? '#EAB308'
                  : em.type === 'FIN_DE_MISSION' ? '#22C55E'
                  : em.type === 'ATTENTE_PRISE_EN_CHARGE' || em.type === 'WAITING_FOR_PICKUP' ? '#FB923C'
                  : em.type?.startsWith('ANOMALY_') ? '#EF4444'
                  : '#C9A24D';
                return (
                <Marker
                  key={`evt-${em.id}`}
                  position={{ lat: em.lat, lng: em.lng }}
                  icon={{
                    path: window.google?.maps?.SymbolPath?.CIRCLE,
                    scale: mapType !== 'roadmap' ? 7 : 5,
                    fillColor: mapType !== 'roadmap' ? '#FFFFFF' : evtColor,
                    fillOpacity: 1,
                    strokeColor: mapType !== 'roadmap' ? evtColor : '#fff',
                    strokeWeight: mapType !== 'roadmap' ? 3 : 1.5,
                  }}
                  title={`${em.label} — ${em.timestamp_local || ''}`}
                />
                );
              })}

              {/* Real-time traffic layer */}
              {showTraffic && <TrafficLayer />}

              {/* GPS Signal Quality Heatmap */}
              <GpsHeatmapLayer map={mapInstance} visible={showHeatmap} />
            </GoogleMap>

            {/* Speed-colour legend (excess vs limit) — bottom-left */}
            {speedTraceSegments.length > 0 && (
              <div className="absolute bottom-3 left-3 z-10 px-2.5 py-2 rounded-lg bg-black/60 backdrop-blur-sm border border-white/10"
                data-testid="speed-color-legend">
                <div className="text-[9px] uppercase tracking-wide text-white/50 mb-1">Vitesse</div>
                <div className="flex flex-col gap-0.5">
                  {SPEED_TRACE_LEGEND.map((st, i) => (
                    <div key={i} className="flex items-center gap-1.5">
                      <span className="w-3 h-1.5 rounded-sm" style={{ backgroundColor: st.color }} />
                      <span className="text-[9px] text-white/70">{st.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Map overlay controls — organized grid */}
            <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5" data-testid="map-controls">
              {/* Row 1: Status + Signal info */}
              <div className="flex items-center gap-1.5">
                <div className="px-2.5 py-1 rounded-full text-[10px] font-medium text-white flex items-center gap-1.5 backdrop-blur-sm"
                  style={{ backgroundColor: statusCfg.color + 'CC' }} data-testid="mission-status-badge">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/80 animate-pulse" />{statusCfg.label}
                </div>
                {realGapBridges.length > 0 && (
                  <div className="px-2 py-1 rounded-full text-[10px] font-medium bg-slate-800/80 backdrop-blur-sm border border-slate-600/30 text-slate-300" data-testid="real-gaps-indicator">
                    {realGapBridges.length} coupure{realGapBridges.length > 1 ? 's' : ''} signal
                  </div>
                )}
              </div>

              {/* Row 2: Map tools */}
              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setShowTraffic(!showTraffic)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-medium backdrop-blur-sm transition-all border ${
                    showTraffic
                      ? 'bg-cyan-500/20 border-cyan-500/30 text-cyan-400'
                      : 'bg-black/60 border-white/10 text-gray-400 hover:text-white hover:bg-black/80'
                  }`}
                  data-testid="traffic-toggle">
                  <Activity className="w-3 h-3 inline mr-1" />Trafic
                </button>

                <button
                  onClick={() => setMapType(prev => prev === 'roadmap' ? 'hybrid' : 'roadmap')}
                  className={`px-2 py-1 rounded-lg text-[10px] font-medium backdrop-blur-sm transition-all border ${
                    mapType !== 'roadmap'
                      ? 'bg-purple-500/20 border-purple-500/30 text-purple-400'
                      : 'bg-black/60 border-white/10 text-gray-400 hover:text-white hover:bg-black/80'
                  }`}
                  data-testid="satellite-toggle">
                  <Satellite className="w-3 h-3 inline mr-1" />Satellite
                </button>

                {locations.length > 1 && (
                  <button
                    onClick={fitBounds}
                    className="px-2 py-1 rounded-lg text-[10px] font-medium backdrop-blur-sm bg-black/60 border border-white/10 text-gray-400 hover:text-white hover:bg-black/80 transition-all"
                    title="Voir tout le trajet"
                    data-testid="fit-bounds-btn">
                    <Maximize2 className="w-3 h-3 inline mr-1" />Trajet
                  </button>
                )}

                {locations.length > 0 && (
                  <button
                    onClick={() => { window.open(`${API}/public/track/${token}/export`, '_blank'); }}
                    className="px-2 py-1 rounded-lg text-[10px] font-medium backdrop-blur-sm bg-black/60 border border-white/10 text-gray-400 hover:text-white hover:bg-black/80 transition-all"
                    title="Exporter en Excel"
                    data-testid="export-excel-btn">
                    <Download className="w-3 h-3 inline mr-1" />Excel
                  </button>
                )}

                <button
                  onClick={() => setShowHeatmap(!showHeatmap)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-medium backdrop-blur-sm transition-all border ${
                    showHeatmap
                      ? 'bg-orange-500/20 border-orange-500/30 text-orange-400'
                      : 'bg-black/60 border-white/10 text-gray-400 hover:text-white hover:bg-black/80'
                  }`}
                  title="Zones à faible signal GPS"
                  data-testid="heatmap-toggle">
                  <Flame className="w-3 h-3 inline mr-1" />Heatmap
                </button>

                {locations.length > 0 && (
                  <button
                    onClick={handleResync}
                    disabled={isResyncing}
                    className={`px-2 py-1 rounded-lg text-[10px] font-medium backdrop-blur-sm transition-all border ${
                      isResyncing
                        ? 'bg-green-500/20 border-green-500/30 text-green-400 animate-pulse'
                        : 'bg-black/60 border-white/10 text-gray-400 hover:text-white hover:bg-black/80'
                    }`}
                    title="Re-synchroniser les points GPS avec la route"
                    data-testid="resync-gps-btn">
                    <RefreshCw className={`w-3 h-3 inline mr-1 ${isResyncing ? 'animate-spin' : ''}`} />
                    {isResyncing ? 'Resync...' : 'Resync GPS'}
                  </button>
                )}
                {resyncResult && (
                  <span className={`px-2 py-1 rounded-lg text-[10px] font-medium backdrop-blur-sm border ${
                    resyncResult.ok ? 'bg-green-500/20 border-green-500/30 text-green-400' : 'bg-red-500/20 border-red-500/30 text-red-400'
                  }`}>{resyncResult.msg}</span>
                )}

                {/* Admin-only quick tools — hidden from end clients */}
                {isAdminViewer && data?.mission_id && (
                  <>
                    <button
                      onClick={() => setAnalysisOpen(true)}
                      className="px-2 py-1 rounded-lg text-[10px] font-medium backdrop-blur-sm bg-[#C9A24D]/15 border border-[#C9A24D]/30 text-[#C9A24D] hover:bg-[#C9A24D]/25 transition-all"
                      title="Analyse du trajet (admin)"
                      data-testid="admin-analysis-btn">
                      <Activity className="w-3 h-3 inline mr-1" />Analyse
                    </button>
                    <button
                      onClick={() => setVideoExportOpen(true)}
                      className="px-2 py-1 rounded-lg text-[10px] font-medium backdrop-blur-sm bg-violet-500/15 border border-violet-500/30 text-violet-300 hover:bg-violet-500/25 transition-all"
                      title="Export Vidéo GPS (admin)"
                      data-testid="admin-video-export-btn">
                      <Video className="w-3 h-3 inline mr-1" />Export Vidéo
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Last update & accuracy overlay */}
            {effectiveLive?.last_update && isLive && (
              <div className="absolute top-3 right-14 z-10 bg-black/70 backdrop-blur-sm rounded-lg px-2.5 py-1 text-[10px] text-gray-300 border border-white/5" data-testid="last-update">
                <Clock className="w-3 h-3 inline mr-1 text-amber-400/70" />Maj: {formatTime(effectiveLive.last_update)}
              </div>
            )}

            {/* ETA overlay on map */}
            {etaInfo && isLive && (
              <div className="absolute bottom-3 left-3 z-10 bg-black/80 backdrop-blur-md rounded-xl px-3 py-2.5 border border-[#C9A24D]/20 shadow-lg shadow-black/30" data-testid="eta-map-overlay">
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-lg bg-[#C9A24D]/15 flex items-center justify-center">
                    <Timer className="w-4 h-4 text-[#C9A24D]" />
                  </div>
                  <div>
                    <p className="text-[9px] text-[#C9A24D]/80 font-medium uppercase tracking-wide">{etaInfo.label}</p>
                    <p className="text-white font-bold text-sm leading-tight">{etaInfo.duration}</p>
                    <p className="text-gray-500 text-[9px]">{etaInfo.distance}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Timeline — Phase 3 */}
          {locations.length > 0 && (
            <TrackingTimeline
              points={cursorPoints.length > 0 ? cursorPoints : locations}
              speedSource={locations}
              events={events}
              onPositionChange={handleReplayPosition}
              onModeChange={handleModeChange}
              isLiveMode={isLive}
              missionActiveStart={data?.mission_active_start}
              missionActiveEnd={data?.mission_active_end}
              missionStatus={mission.status}
            />
          )}
        </div>

        {/* Right sidebar */}
        <div className="lg:w-80 xl:w-96 bg-[#12121f] border-t lg:border-t-0 lg:border-l border-white/5 flex flex-col overflow-hidden max-h-[35vh] lg:max-h-full" data-testid="tracking-sidebar">
          {/* Mission info */}
          <div className="p-4 border-b border-white/5 shrink-0">
            <h2 className="text-white font-semibold text-sm mb-3 flex items-center gap-2">
              <Car className="w-4 h-4 text-[#C9A24D]" />Informations mission
            </h2>
            <div className="flex items-center gap-2.5 mb-3">
              <div className="w-9 h-9 rounded-full bg-[#C9A24D]/10 border border-[#C9A24D]/20 flex items-center justify-center"><User className="w-4 h-4 text-[#C9A24D]" /></div>
              <div>
                <p className="text-white text-sm font-medium" data-testid="driver-name">{driver.name || 'Chauffeur'}</p>
                <p className="text-gray-500 text-[10px]">Chauffeur Y GO</p>
              </div>
            </div>
            <div className="space-y-2.5 text-xs">
              {/* Date de la mission */}
              {headerDate && (
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-[#C9A24D]/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Clock className="w-3 h-3 text-[#C9A24D]" />
                  </div>
                  <div><p className="text-gray-500 text-[10px]">Date</p><p className="text-white font-medium capitalize" data-testid="mission-date-sidebar">{headerDate}</p></div>
                </div>
              )}
              {mission.pickup_address && (
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-emerald-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="w-3 h-3 text-emerald-400" />
                  </div>
                  <div><p className="text-gray-500 text-[10px]">Prise en charge</p><p className="text-white leading-tight" data-testid="pickup-address">{mission.pickup_address}</p></div>
                </div>
              )}
              {mission.dropoff_address && (
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-red-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <MapPin className="w-3 h-3 text-red-400" />
                  </div>
                  <div><p className="text-gray-500 text-[10px]">Destination</p><p className="text-white leading-tight" data-testid="dropoff-address">{mission.dropoff_address}</p></div>
                </div>
              )}
              {/* ETA en temps réel */}
              {etaInfo && isLive && (
                <div className="flex items-start gap-2.5 bg-[#C9A24D]/8 border border-[#C9A24D]/15 rounded-lg p-2.5 mt-1" data-testid="eta-info">
                  <div className="w-5 h-5 rounded-full bg-[#C9A24D]/15 flex items-center justify-center shrink-0 mt-0.5">
                    <Timer className="w-3 h-3 text-[#C9A24D]" />
                  </div>
                  <div>
                    <p className="text-[#C9A24D] font-medium text-[10px] uppercase tracking-wide">{etaInfo.label}</p>
                    <p className="text-white font-semibold text-sm">{etaInfo.duration}</p>
                    <p className="text-gray-500 text-[10px]">{etaInfo.distance}</p>
                  </div>
                </div>
              )}
              {(mission.pickup_datetime || mission.start_time) && (
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-blue-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Clock className="w-3 h-3 text-blue-400" />
                  </div>
                  <div><p className="text-gray-500 text-[10px]">Horaire prévu</p><p className="text-white" data-testid="mission-time">{formatDateTime(mission.pickup_datetime || mission.start_time)}</p></div>
                </div>
              )}
              {mission.excursion_name && (
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-amber-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <Navigation className="w-3 h-3 text-amber-400" />
                  </div>
                  <div><p className="text-gray-500 text-[10px]">Excursion</p><p className="text-white">{mission.excursion_name}</p></div>
                </div>
              )}
              {mission.client_name && (
                <div className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-gray-500/10 flex items-center justify-center shrink-0 mt-0.5">
                    <User className="w-3 h-3 text-gray-400" />
                  </div>
                  <div><p className="text-gray-500 text-[10px]">Client</p><p className="text-white">{mission.client_name}</p></div>
                </div>
              )}
            </div>
          </div>

          {/* Events + Chat container */}
          <div className="flex-1 overflow-hidden flex flex-col min-h-0">
            {/* Events - collapsible */}
            <div className={`${showEvents ? 'max-h-[40%]' : ''} flex flex-col shrink-0`}>
              <button onClick={() => setShowEvents(!showEvents)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-white hover:bg-white/5 transition-colors shrink-0"
                data-testid="toggle-events-btn">
                <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-[#C9A24D]" />Événements ({events.filter(e => String(e.type || '').toUpperCase() !== 'MISSION_ACCEPTEE').length})</span>
                {showEvents ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {showEvents && (
                <div className="px-4 pb-3 overflow-y-auto" data-testid="events-list">
                  {events.length === 0 ? (
                    <p className="text-gray-500 text-xs text-center py-4">Aucun événement pour le moment</p>
                  ) : (
                    <div className="space-y-0">
                      {events
                        .filter(evt => String(evt.type || '').toUpperCase() !== 'MISSION_ACCEPTEE')
                        .map((evt, idx, filteredArr) => {
                          const dotColor = getEventStatusColor(evt.type);
                          const isAlert = evt.type === 'DISTANCE_ALERT';
                          const isAnomaly = evt.type?.startsWith('ANOMALY');
                          const isStop = evt.type === 'ARRET' || evt.type === 'ARRET_AUTO_DETECTE';
                          return (
                        <div key={idx} className="flex items-start gap-3 relative" data-testid={`event-${idx}`}>
                          <div className="flex flex-col items-center shrink-0">
                            <div
                              className="w-2.5 h-2.5 rounded-full z-10 border-2"
                              style={{
                                backgroundColor: dotColor,
                                borderColor: withAlpha(dotColor, '4D'),
                                boxShadow: `0 0 6px ${withAlpha(dotColor, '55')}`,
                              }}
                            />
                            {idx < filteredArr.length - 1 && <div className="w-px flex-1 bg-white/5 min-h-[24px]" />}
                          </div>
                          <div className="pb-4 min-w-0">
                            <p className="text-xs font-medium" style={{ color: dotColor }}>
                              {(isAlert || isAnomaly || isStop) && <AlertTriangle className="w-3 h-3 inline mr-1" style={{ color: dotColor }} />}
                              {evt.label}
                            </p>
                            <p className="text-gray-500 text-[10px]">{evt.timestamp_local || formatDateTime(evt.timestamp)}</p>
                            {evt.address && <p className="text-gray-400 text-[10px] mt-0.5 leading-tight">{evt.address}</p>}
                            {evt.note && <p className="text-[10px] mt-0.5 italic" style={{ color: withAlpha(dotColor, 'AA') }}>{evt.note}</p>}
                          </div>
                        </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Chat section */}
            <div className="flex-1 flex flex-col min-h-0 border-t border-white/5" data-testid="tracking-chat">
              <button onClick={() => setShowChat(!showChat)}
                className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-white hover:bg-white/5 transition-colors shrink-0"
                data-testid="toggle-chat-btn">
                <span className="flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-[#C9A24D]" />
                  Chat opérations
                  {chatMessages.length > 0 && <span className="text-[10px] text-gray-500 font-normal">({chatMessages.length})</span>}
                </span>
                {showChat ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </button>
              {showChat && (
                <div className="flex-1 flex flex-col min-h-0">
                  {/* Messages area with scroll */}
                  <div className="flex-1 overflow-y-auto px-4 py-2 space-y-2" data-testid="chat-messages">
                    {chatMessages.length === 0 ? (
                      <p className="text-gray-500 text-xs text-center py-6">Aucun message</p>
                    ) : (
                      chatMessages.map((msg) => {
                        const isDriver = msg.sender_role === 'driver';
                        const isAdmin = msg.sender_role === 'admin' || msg.sender_role === 'dispatcher';
                        const isTracking = msg.sender_role === 'tracking' || msg.sender_role === 'operations';
                        const timeStr = msg.created_at ? new Date(msg.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '';
                        return (
                          <div key={msg.id} className={`flex ${isDriver ? 'justify-start' : 'justify-end'}`} data-testid={`chat-msg-${msg.id}`}>
                            <div className={`max-w-[85%] rounded-lg px-3 py-1.5 ${
                              isDriver ? 'bg-white/5 border border-white/10' :
                              isTracking ? 'bg-[#C9A24D]/15 border border-[#C9A24D]/20' :
                              'bg-blue-600/20 border border-blue-500/20'
                            }`}>
                              <p className={`text-[10px] font-medium mb-0.5 ${
                                isDriver ? 'text-emerald-400' :
                                isTracking ? 'text-[#C9A24D]' :
                                'text-blue-400'
                              }`}>
                                {msg.sender_name || (isDriver ? 'Chauffeur' : isTracking ? 'Suivi' : 'Dispatch')}
                              </p>
                              <p className="text-white text-xs leading-relaxed">{msg.content}</p>
                              <p className="text-gray-600 text-[9px] mt-0.5 text-right">{timeStr}</p>
                            </div>
                          </div>
                        );
                      })
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {/* Name prompt — shown when not identified */}
                  {!chatSenderName ? (
                    <div className="px-4 py-3 border-t border-white/5 shrink-0 bg-[#1a1a2e]">
                      <p className="text-white text-xs mb-2">Identifiez-vous pour participer au chat :</p>
                      <div className="flex gap-2">
                        <input
                          data-testid="chat-name-input"
                          type="text"
                          value={nameInput}
                          onChange={e => setNameInput(e.target.value)}
                          onKeyDown={e => e.key === 'Enter' && handleSaveName()}
                          placeholder="Votre prénom..."
                          className="flex-1 h-8 rounded-md bg-[#0B0B0B] border border-white/10 px-3 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#C9A24D]/40"
                          autoFocus
                        />
                        <button
                          data-testid="chat-save-name-btn"
                          onClick={handleSaveName}
                          disabled={!nameInput.trim()}
                          className="h-8 px-3 rounded-md bg-[#C9A24D] text-[#0B0B0B] text-xs font-medium hover:bg-[#E6C77A] transition disabled:opacity-30"
                        >Valider</button>
                      </div>
                    </div>
                  ) : (
                    <div className="px-4 py-2 border-t border-white/5 shrink-0 bg-[#0d0d1a]">
                      <p className="text-[9px] text-gray-600 mb-1 flex items-center gap-1">
                        <User className="w-2.5 h-2.5" />
                        {chatSenderName}
                        <button onClick={() => { setChatSenderName(''); localStorage.removeItem('ygo_chat_name'); }} className="text-gray-500 hover:text-[#C9A24D] ml-1 underline">changer</button>
                      </p>
                      <div className="flex gap-2">
                        <input
                          data-testid="chat-input"
                          type="text"
                          value={chatInput}
                          onChange={e => setChatInput(e.target.value)}
                          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }}
                          placeholder="Écrire un message..."
                          className="flex-1 h-8 rounded-md bg-[#0B0B0B] border border-white/10 px-3 text-xs text-white placeholder:text-gray-600 focus:outline-none focus:border-[#C9A24D]/40"
                          disabled={chatSending}
                        />
                        <button
                          data-testid="chat-send-btn"
                          onClick={handleSendChat}
                          disabled={chatSending || !chatInput.trim()}
                          className="h-8 w-8 rounded-md bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B] flex items-center justify-center transition disabled:opacity-30"
                        >
                          <Send className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="p-3 border-t border-white/5 shrink-0 bg-[#0a0a14]">
            <div className="flex items-center justify-between text-[10px] text-gray-600">
              <span className="flex items-center gap-1">
                <Activity className="w-2.5 h-2.5" />{locations.length} points GPS
                {mergedPolylinePaths.length > 0 && ` / ${mergedPolylinePaths.length} tracé${mergedPolylinePaths.length > 1 ? 's' : ''}`}
              </span>
              <span>{isLive ? 'Temps réel' : 'Mode replay'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Admin-only modals — only mounted when an admin/dispatcher is viewing */}
      {isAdminViewer && videoExportOpen && data?.mission_id && (
        <GpsVideoExportModal
          fetchPointsUrl={`${API}/admin/missions/${data.mission_id}/locations`}
          driverName={driver?.name || 'Chauffeur'}
          subtitle={`Mission ${data?.mission_ref || data.mission_id}${data?.mission_type ? ` · ${data.mission_type}` : ''}`}
          filename={`mission_${data?.mission_ref || data.mission_id}`}
          onClose={() => setVideoExportOpen(false)}
        />
      )}
      {isAdminViewer && (
        <MissionAnalysisModal
          open={analysisOpen}
          missionId={data?.mission_id}
          missionRef={data?.mission_ref || data?.mission_id}
          onClose={() => setAnalysisOpen(false)}
        />
      )}
    </div>
  );
}
